/**
 * ContentScheduler — Metricool-style content planner.
 * Weekly calendar view + "Create post" modal with live preview.
 */

import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, uploadSchedulerMedia } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/lib/store";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  CalendarDays,
  Plus,
  Send,
  Trash2,
  AlertCircle,
  Facebook,
  Instagram,
  ImagePlus,
  Video,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Smile,
  MapPin,
  Link2,
  Smartphone,
  Monitor,
  List,
  Upload,
  FolderOpen,
  Linkedin,
  Youtube,
  Music2,
  Sparkles,
  Search,
  SlidersHorizontal,
  Cloud,
  Check,
  Film,
  HardDrive,
  RefreshCw,
  Home,
  Plug,
  AtSign,
  GalleryHorizontalEnd,
} from "lucide-react";
import { Reorder } from "framer-motion";
import type {
  ScheduledPost,
  ScheduledPostPlatform,
  ScheduledPostMediaType,
  ScheduledMetaPostType,
  SchedulerPresets,
} from "@shared/schema";
import { DEFAULT_SCHEDULER_PRESETS } from "@shared/schema";
import { VoiceButton } from "@/components/ui/voice-button";
import { AiGeneratingOverlay } from "@/components/ui/ai-generating-overlay";
import CloudStoragePicker, { type SelectedCloudFile } from "@/components/cloud-storage-picker";
import BulkSchedulerDialog from "@/components/bulk-scheduler-dialog";
import { PresetsPanel } from "@/components/scheduler/PresetsPanel";
import { getTikTokValidity } from "@/components/scheduler/presets/tiktok-compliance";
import { PreviewPanel } from "@/components/scheduler/PreviewPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ListSkeleton } from "@/components/skeletons";

// ─── Constants ───

const DRIVE_DRAG_MIME = "application/x-viralpartners-drive-file";

const PLATFORM_OPTIONS: {
  id: ScheduledPostPlatform;
  label: string;
  icon: typeof Facebook;
  color: string;
}[] = [
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "#E4405F" },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  { id: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000" },
  { id: "tiktok", label: "TikTok", icon: Music2, color: "#25F4EE" },
  // YouTube + LinkedIn widoczne tylko dla super_admin (gating w komponencie)
];

const BEST_TIME_HEATMAP_COLORS: Record<ScheduledPostPlatform, [number, number, number]> = {
  facebook: [58, 131, 238],
  instagram: [255, 91, 164],
  linkedin: [126, 136, 145],
  youtube: [255, 94, 94],
  tiktok: [92, 98, 106],
};

const STATUS_CONFIG: Record<
  string,
  { labelKey: string; dotColor: string; chipBorder: string; chipBg: string }
> = {
  draft: {
    labelKey: "contentScheduler.status.draft",
    dotColor: "bg-white/30",
    chipBorder: "border-white/[0.12]",
    chipBg: "bg-[#1b2432]/95",
  },
  scheduled: {
    labelKey: "contentScheduler.status.scheduled",
    dotColor: "bg-yellow-400",
    chipBorder: "border-yellow-400/30",
    chipBg: "bg-[#1b1f18]/95",
  },
  publishing: {
    labelKey: "contentScheduler.status.publishing",
    dotColor: "bg-blue-400",
    chipBorder: "border-blue-400/30",
    chipBg: "bg-[#131a26]/95",
  },
  published: {
    labelKey: "contentScheduler.status.published",
    dotColor: "bg-green-400",
    chipBorder: "border-green-400/30",
    chipBg: "bg-[#131f18]/95",
  },
  failed: {
    labelKey: "contentScheduler.status.failed",
    dotColor: "bg-red-400",
    chipBorder: "border-red-400/30",
    chipBg: "bg-[#26131a]/95",
  },
};

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 00:00 – 23:00

const FB_CHAR_LIMIT = 63206;
const IG_CHAR_LIMIT = 2200;
const LI_CHAR_LIMIT = 3000;
const YT_CHAR_LIMIT = 5000;
const TIKTOK_CHAR_LIMIT = 2200;

const CHAR_LIMITS: Record<ScheduledPostPlatform, number> = {
  facebook: FB_CHAR_LIMIT,
  instagram: IG_CHAR_LIMIT,
  linkedin: LI_CHAR_LIMIT,
  youtube: YT_CHAR_LIMIT,
  tiktok: TIKTOK_CHAR_LIMIT,
};

const META_POST_TYPE_OPTIONS: Record<ScheduledPostPlatform, ScheduledMetaPostType[]> = {
  facebook: ["post", "reel"],
  instagram: ["post", "reel", "trial_reel", "story"],
  linkedin: ["post"],
  youtube: ["post", "reel"],
  tiktok: ["post", "reel"],
};

const META_POST_TYPE_LABELS: Record<ScheduledMetaPostType, string> = {
  post: "Post",
  reel: "Reel",
  trial_reel: "Trial Reel",
  story: "Story",
};

const PLATFORM_CONNECT_CONFIG = {
  facebook: {
    connectKey: "meta",
    connectLabelKey: "contentScheduler.platformConnect.facebook.label",
    connectDescriptionKey: "contentScheduler.platformConnect.facebook.description",
  },
  instagram: {
    connectKey: "meta",
    connectLabelKey: "contentScheduler.platformConnect.instagram.label",
    connectDescriptionKey: "contentScheduler.platformConnect.instagram.description",
  },
  youtube: {
    connectKey: "youtube",
    connectLabelKey: "contentScheduler.platformConnect.youtube.label",
    connectDescriptionKey: "contentScheduler.platformConnect.youtube.description",
  },
  linkedin: {
    connectKey: "linkedin",
    connectLabelKey: "contentScheduler.platformConnect.linkedin.label",
    connectDescriptionKey: "contentScheduler.platformConnect.linkedin.description",
  },
  tiktok: {
    connectKey: "tiktok",
    connectLabelKey: "contentScheduler.platformConnect.tiktok.label",
    connectDescriptionKey: "contentScheduler.platformConnect.tiktok.description",
  },
} as const;

const CONNECT_URLS: Partial<Record<"meta" | "youtube" | "tiktok" | "linkedin", string>> = {
  meta: "/api/integrations/meta/connect",
  youtube: "/api/integrations/youtube/connect",
  tiktok: "/api/integrations/tiktok/connect",
  linkedin: "/api/integrations/linkedin/connect",
};

type ViewMode = "calendar" | "list";
type ScheduleStatusFilter = "draft" | "scheduled" | "publishing" | "published" | "failed";
type ComposerAction = "schedule" | "draft" | "publish_now";

// ─── Types ───

interface MetaPage {
  id: string;
  name: string;
  instagramBusinessAccount?: { id: string; name?: string; username?: string };
}

interface PostFormData {
  platform: ScheduledPostPlatform;
  postType: ScheduledMetaPostType;
  platformPostTypes: Partial<Record<ScheduledPostPlatform, ScheduledMetaPostType>>;
  title: string;
  caption: string;
  mediaType: ScheduledPostMediaType;
  mediaUrls: string[];
  pageId: string;
  pageName: string;
  igAccountId: string;
  igAccountName: string;
  scheduledAt: string;
  presets: SchedulerPresets;
  threadsSelected: boolean;
}

interface TemporaryMediaItem {
  id: string;
  source: "local" | "cloud";
  name: string;
  mediaType: "image" | "video";
  previewUrl?: string;
  provider?: "google_drive";
  cloudFileId?: string;
  sizeLabel?: string;
  uploading?: boolean;
  uploadError?: string;
  progress?: number;
}

interface SchedulerDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  isFolder: boolean;
  thumbnailUrl?: string;
  modifiedAt?: string;
}

interface SchedulerDriveStatusResponse {
  google_drive: {
    connected: boolean;
    email?: string;
    connectedAt?: string;
  };
}

interface CloudImportResponse {
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
}

interface PlannerFilters {
  search: string;
  platforms: ScheduledPostPlatform[];
  statuses: ScheduleStatusFilter[];
}

interface SchedulerBestTimeSlot {
  day: number;
  hour: number;
  score: number;
  percent: number;
}

interface SchedulerBestTimesResponse {
  platform: ScheduledPostPlatform;
  timezone: string;
  source: string;
  generatedAt: string;
  slots: SchedulerBestTimeSlot[];
}

interface ConnectedSchedulerPlatform {
  platform: ScheduledPostPlatform;
  connected: boolean;
  accountId?: string | null;
  accountName?: string | null;
  accountThumbnail?: string | null;
  profileName?: string | null;
  provider: "meta" | "youtube" | "tiktok" | "linkedin";
}

interface ConnectedSchedulerPlatformsResponse {
  platforms: ConnectedSchedulerPlatform[];
}

interface SchedulerPostsResponse {
  posts: ScheduledPost[];
  hasMore?: boolean;
  nextCursor?: string | null;
}

interface SchedulerPagesResponse {
  pages: MetaPage[];
}

interface LinkedInOrganization {
  id: string;
  urn: string;
  name: string;
  role?: string;
}

interface SchedulerLinkedInOrganizationsResponse {
  organizations: LinkedInOrganization[];
}

interface SchedulerCreatePostResponse {
  post: ScheduledPost;
}

interface SchedulerCaptionResponse {
  caption?: string;
}

interface OAuthConnectResponse {
  url?: string;
}

interface GraphicsLibraryResponse {
  items: LibraryGraphic[];
}

const EMPTY_FORM: PostFormData = {
  platform: "facebook",
  postType: "post",
  platformPostTypes: {},
  title: "",
  caption: "",
  mediaType: "text",
  mediaUrls: [],
  pageId: "",
  pageName: "",
  igAccountId: "",
  igAccountName: "",
  scheduledAt: "",
  presets: DEFAULT_SCHEDULER_PRESETS,
  threadsSelected: false,
};

const ALL_SCHEDULER_PLATFORMS: ScheduledPostPlatform[] = [
  "facebook",
  "instagram",
  "linkedin",
  "youtube",
  "tiktok",
];
const ALL_SCHEDULER_STATUSES: ScheduleStatusFilter[] = [
  "draft",
  "scheduled",
  "publishing",
  "published",
  "failed",
];

const BEST_TIME_FALLBACK_SLOTS: Record<ScheduledPostPlatform, SchedulerBestTimeSlot[]> = {
  facebook: [
    { day: 1, hour: 12, score: 82, percent: 16 },
    { day: 3, hour: 13, score: 88, percent: 18 },
    { day: 1, hour: 14, score: 72, percent: 14 },
    { day: 3, hour: 12, score: 76, percent: 15 },
  ],
  instagram: [
    { day: 3, hour: 20, score: 94, percent: 21 },
    { day: 5, hour: 20, score: 91, percent: 20 },
    { day: 1, hour: 19, score: 78, percent: 16 },
    { day: 2, hour: 21, score: 74, percent: 15 },
  ],
  linkedin: [
    { day: 3, hour: 10, score: 90, percent: 22 },
    { day: 5, hour: 10, score: 84, percent: 19 },
    { day: 2, hour: 9, score: 76, percent: 16 },
    { day: 4, hour: 11, score: 70, percent: 14 },
  ],
  youtube: [
    { day: 2, hour: 16, score: 88, percent: 20 },
    { day: 3, hour: 16, score: 86, percent: 19 },
    { day: 1, hour: 18, score: 78, percent: 16 },
    { day: 4, hour: 21, score: 72, percent: 14 },
  ],
  tiktok: [
    { day: 1, hour: 18, score: 87, percent: 19 },
    { day: 2, hour: 18, score: 84, percent: 18 },
    { day: 5, hour: 22, score: 79, percent: 16 },
    { day: 6, hour: 20, score: 73, percent: 14 },
  ],
};

// ─── Date helpers ───

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatWeekRange(weekStart: Date, monthNames: string[]): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const sm = monthNames[weekStart.getMonth()];
  const em = monthNames[weekEnd.getMonth()];
  if (sm === em)
    return `${weekStart.getDate()} – ${weekEnd.getDate()} ${sm} ${weekEnd.getFullYear()}`;
  return `${weekStart.getDate()} ${sm} – ${weekEnd.getDate()} ${em} ${weekEnd.getFullYear()}`;
}

function toLocalDatetimeValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getIsoDay(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

function getNextBestTime(slots: SchedulerBestTimeSlot[], fromDate = new Date()): Date | null {
  if (slots.length === 0) return null;

  const sortedSlots = [...slots].sort(
    (a, b) => b.score - a.score || a.day - b.day || a.hour - b.hour
  );
  let bestCandidate: Date | null = null;
  const minimumDate = new Date(fromDate.getTime() + 5 * 60_000);

  for (let offset = 0; offset <= 14; offset++) {
    const base = new Date(fromDate);
    base.setDate(fromDate.getDate() + offset);

    for (const slot of sortedSlots) {
      if (getIsoDay(base) !== slot.day) continue;
      const candidate = new Date(base);
      candidate.setHours(slot.hour, 0, 0, 0);
      if (candidate <= minimumDate) continue;
      if (!bestCandidate || candidate < bestCandidate) {
        bestCandidate = candidate;
      }
    }

    if (bestCandidate) return bestCandidate;
  }

  return null;
}

function getDayDistance(a: number, b: number): number {
  const diff = Math.abs(a - b);
  return Math.min(diff, 7 - diff);
}

function buildBestTimeHeatmap(slots: SchedulerBestTimeSlot[]): Map<string, SchedulerBestTimeSlot> {
  const heatmap = new Map<string, SchedulerBestTimeSlot>();
  if (slots.length === 0) return heatmap;

  for (let day = 1; day <= 7; day++) {
    for (const hour of HOURS) {
      const exactSlot = slots.find((slot) => slot.day === day && slot.hour === hour);
      if (exactSlot) {
        heatmap.set(`${day}-${hour}`, exactSlot);
        continue;
      }

      const weightedScore = slots.reduce((best, slot) => {
        const hourDistance = Math.abs(hour - slot.hour);
        const dayDistance = getDayDistance(day, slot.day);
        const hourFactor = Math.max(0.18, 1 - hourDistance / 9);
        const dayFactor = Math.max(0.38, 1 - dayDistance * 0.18);
        const score = slot.score * hourFactor * dayFactor;
        return Math.max(best, score);
      }, 0);

      const score = Math.max(8, Math.min(95, Math.round(weightedScore)));
      heatmap.set(`${day}-${hour}`, {
        day,
        hour,
        score,
        percent: Math.max(1, Math.min(99, Math.round(score * 0.78))),
      });
    }
  }

  return heatmap;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, unitIndex);
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function isRenderableUrl(url?: string): boolean {
  if (!url) return false;
  return (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:") ||
    url.startsWith("data:")
  );
}

function inferTemporaryMediaType(mimeType: string): "image" | "video" {
  return mimeType.startsWith("video/") ? "video" : "image";
}

// Wykrywanie wideo po rozszerzeniu URL — spójne z backendem
// (apps/api/modules/meta/meta-publish-service.ts publishInstagramCarousel).
const VIDEO_URL_RE = /\.(mp4|mov|avi|webm|m4v)(\?|$)/i;
function isVideoMediaUrl(url: string): boolean {
  return VIDEO_URL_RE.test(url);
}

// Deterministycznie wyprowadza mediaType z listy URL-i (bez wideo + ≥2 = karuzela).
function deriveMediaType(urls: string[]): ScheduledPostMediaType {
  if (urls.length === 0) return "text";
  if (urls.some(isVideoMediaUrl)) return "video";
  return urls.length >= 2 ? "carousel" : "image";
}

function getPostCalendarDate(post: ScheduledPost): Date | null {
  const rawDate =
    post.scheduledAt ??
    post.publishedAt ??
    (post.status === "failed" || post.status === "publishing" ? post.updatedAt : null) ??
    null;
  if (!rawDate) return null;
  const date = new Date(rawDate);
  return isNaN(date.getTime()) ? null : date;
}

// ═══════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════

export default function ContentScheduler() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [modalSessionKey, setModalSessionKey] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPostStatus, setEditingPostStatus] = useState<string>("draft");
  const [form, setForm] = useState<PostFormData>(EMPTY_FORM);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [weekOffset, setWeekOffset] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [showBestTimes, setShowBestTimes] = useState(true);
  const [showBestTimePercent, setShowBestTimePercent] = useState(false);
  const [drivePanelOpen, setDrivePanelOpen] = useState(false);
  const [driveImportingFileId, setDriveImportingFileId] = useState<string | null>(null);
  const [bestTimePlatform, setBestTimePlatform] = useState<ScheduledPostPlatform>(
    ALL_SCHEDULER_PLATFORMS[0]
  );
  const [filters, setFilters] = useState<PlannerFilters>({
    search: "",
    platforms: [...ALL_SCHEDULER_PLATFORMS],
    statuses: [...ALL_SCHEDULER_STATUSES],
  });
  const [temporaryMedia, setTemporaryMedia] = useState<TemporaryMediaItem[]>([]);
  const temporaryMediaRef = useRef<TemporaryMediaItem[]>([]);
  const bestTimePlatformTouchedRef = useRef(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editBatchId, setEditBatchId] = useState<string | null>(null);

  const today = new Date();
  const currentWeekStart = useMemo(() => {
    const ws = getWeekStart(new Date());
    ws.setDate(ws.getDate() + weekOffset * 7);
    return ws;
  }, [weekOffset]);
  const weekDays = useMemo(() => getWeekDays(currentWeekStart), [currentWeekStart]);

  // Active client scope: when an agency owner / employee picks a client in the sidebar,
  // scheduler operations must be performed against that client's social integrations and
  // post pool — not the auth'd user's. Backend resolves via `forUserId` after access check.
  const activeClientUserId = useStore((s) => s.selectedClientUserId);
  const pendingCrosspost = useStore((s) => s.pendingCrosspost);
  const setPendingCrosspost = useStore((s) => s.setPendingCrosspost);
  const crosspostConsumedRef = useRef(false);
  const forUserParam = activeClientUserId
    ? `forUserId=${encodeURIComponent(activeClientUserId)}`
    : "";
  const withForUser = (path: string) => {
    if (!activeClientUserId) return path;
    return path.includes("?") ? `${path}&${forUserParam}` : `${path}?${forUserParam}`;
  };

  // Queries
  const { data: postsData, isLoading: postsLoading } = useQuery<SchedulerPostsResponse>({
    queryKey: ["/api/scheduler", activeClientUserId ?? null],
    queryFn: () =>
      apiRequest("GET", withForUser("/api/scheduler")) as Promise<SchedulerPostsResponse>,
    refetchInterval: 30_000,
  });
  const { data: pagesData, isLoading: pagesLoading } = useQuery<SchedulerPagesResponse>({
    queryKey: ["/api/scheduler/pages", activeClientUserId ?? null],
    queryFn: () =>
      apiRequest("GET", withForUser("/api/scheduler/pages")) as Promise<SchedulerPagesResponse>,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const { data: connectedPlatformsData, isLoading: connectedPlatformsLoading } =
    useQuery<ConnectedSchedulerPlatformsResponse>({
      queryKey: ["/api/scheduler/connected-platforms", activeClientUserId ?? null],
      queryFn: () =>
        apiRequest(
          "GET",
          withForUser("/api/scheduler/connected-platforms")
        ) as Promise<ConnectedSchedulerPlatformsResponse>,
      staleTime: 30_000,
      retry: false,
    });
  const { data: linkedInOrganizationsData, isLoading: linkedInOrganizationsLoading } =
    useQuery<SchedulerLinkedInOrganizationsResponse>({
      queryKey: ["/api/scheduler/linkedin-organizations"],
      queryFn: () =>
        apiRequest(
          "GET",
          "/api/scheduler/linkedin-organizations"
        ) as Promise<SchedulerLinkedInOrganizationsResponse>,
      staleTime: 5 * 60 * 1000,
      retry: false,
      enabled: Boolean(
        connectedPlatformsData?.platforms.some(
          (item) => item.connected && item.platform === "linkedin"
        )
      ),
    });
  const { data: bestTimesData } = useQuery<SchedulerBestTimesResponse>({
    queryKey: ["/api/scheduler/best-times", bestTimePlatform, activeClientUserId ?? null],
    queryFn: () =>
      apiRequest(
        "GET",
        withForUser(`/api/scheduler/best-times?platform=${bestTimePlatform}`)
      ) as Promise<SchedulerBestTimesResponse>,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const posts: ScheduledPost[] = useMemo(() => postsData?.posts ?? [], [postsData?.posts]);
  const pages: MetaPage[] = pagesData?.pages ?? [];
  const linkedInOrganizations = linkedInOrganizationsData?.organizations ?? [];
  const linkedInAccount = connectedPlatformsData?.platforms.find(
    (item) => item.platform === "linkedin"
  );
  const linkedInProfileName =
    linkedInAccount?.profileName ?? linkedInAccount?.accountName ?? "Twój profil";
  const searchValue = filters.search.trim().toLowerCase();

  const connectedPlatforms = useMemo(() => {
    return (connectedPlatformsData?.platforms ?? [])
      .filter((item) => item.connected)
      .map((item) => item.platform);
  }, [connectedPlatformsData?.platforms]);

  const connectablePlatforms = useMemo(() => {
    const allPlatforms: ScheduledPostPlatform[] = [
      "facebook",
      "instagram",
      "youtube",
      "linkedin",
      "tiktok",
    ];
    return allPlatforms.filter((platform) => !connectedPlatforms.includes(platform));
  }, [connectedPlatforms]);

  const bestTimePlatformOptions = useMemo(() => {
    return connectedPlatforms.length > 0 ? connectedPlatforms : ALL_SCHEDULER_PLATFORMS;
  }, [connectedPlatforms]);

  useEffect(() => {
    if (bestTimePlatformTouchedRef.current && bestTimePlatformOptions.includes(bestTimePlatform))
      return;
    const defaultPlatform = connectedPlatforms[0] ?? ALL_SCHEDULER_PLATFORMS[0];
    setBestTimePlatform(defaultPlatform);
    bestTimePlatformTouchedRef.current = false;
  }, [bestTimePlatform, bestTimePlatformOptions, connectedPlatforms]);

  // Crosspost handoff from Analytics: open the composer prefilled with the imported
  // media + caption, targeting every connected platform EXCEPT the source. Wait for the
  // connected-platforms query to settle so the platform prefill is accurate; consume the
  // store payload once (ref guard + clearing it) so a re-render can't reopen the modal.
  useEffect(() => {
    if (crosspostConsumedRef.current || !pendingCrosspost || connectedPlatformsLoading) return;
    crosspostConsumedRef.current = true;

    const targets = connectedPlatforms.filter((p) => p !== pendingCrosspost.sourcePlatform);
    const platformPostTypes = Object.fromEntries(targets.map((p) => [p, "reel"])) as Partial<
      Record<ScheduledPostPlatform, ScheduledMetaPostType>
    >;

    setForm({
      ...EMPTY_FORM,
      caption: pendingCrosspost.caption,
      mediaType: pendingCrosspost.mediaType,
      mediaUrls: [pendingCrosspost.mediaUrl],
      platformPostTypes,
      platform: targets[0] ?? EMPTY_FORM.platform,
      postType: "reel",
    });
    setTemporaryMedia([]);
    setEditingId(null);
    setEditingPostStatus("draft");
    setModalSessionKey((key) => key + 1);
    setShowCreateModal(true);
    setPendingCrosspost(null);
  }, [pendingCrosspost, connectedPlatforms, connectedPlatformsLoading, setPendingCrosspost]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesSearch =
        !searchValue ||
        (post.caption || "").toLowerCase().includes(searchValue) ||
        (post.pageName || "").toLowerCase().includes(searchValue) ||
        post.platform.toLowerCase().includes(searchValue);
      const matchesPlatform = filters.platforms.includes(post.platform as ScheduledPostPlatform);
      const matchesStatus = filters.statuses.includes(post.status as ScheduleStatusFilter);
      return matchesSearch && matchesPlatform && matchesStatus;
    });
  }, [filters.platforms, filters.statuses, posts, searchValue]);

  const bestTimeSlots = useMemo(
    () => bestTimesData?.slots ?? BEST_TIME_FALLBACK_SLOTS[bestTimePlatform] ?? [],
    [bestTimesData?.slots, bestTimePlatform]
  );
  const bestTimeSource = bestTimesData?.source ?? "benchmark_seed";

  const bestTimeLookup = useMemo(() => {
    return buildBestTimeHeatmap(bestTimeSlots);
  }, [bestTimeSlots]);

  const resolveDefaultComposerPlatform = useCallback((): ScheduledPostPlatform | null => {
    if (connectedPlatforms.length === 1) return connectedPlatforms[0];
    if (connectedPlatforms.includes(bestTimePlatform)) return bestTimePlatform;
    return connectedPlatforms[0] ?? null;
  }, [bestTimePlatform, connectedPlatforms]);

  const runOAuthPopup = useCallback(
    async (connectUrl: string, popupName: string) => {
      const width = 640;
      const height = 760;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        "about:blank",
        popupName,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
      if (popup) {
        popup.document.write(
          `<html><head><title>${t("contentScheduler.oauth.connectingTitle")}</title></head><body style="background:#101218;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>${t("contentScheduler.oauth.preparingConnection")}</p></body></html>`
        );
        popup.document.close();
      }

      try {
        const data = (await apiRequest("GET", connectUrl)) as OAuthConnectResponse;
        if (data?.url && popup && !popup.closed) {
          popup.location.href = data.url;
        }
      } catch (error) {
        if (popup && !popup.closed) {
          popup.document.body.innerHTML = `<div style="padding:24px;font-family:sans-serif"><h3>${t("contentScheduler.oauth.connectionError")}</h3><p>${t("contentScheduler.oauth.closeAndRetry")}</p></div>`;
        }
        toast({
          title: t("contentScheduler.oauth.connectAccount"),
          description: t("contentScheduler.oauth.closeWindowAndRetry"),
          variant: "destructive",
        });
      }
    },
    [t, toast]
  );

  const openOAuthConnect = useCallback(
    async (platform: ScheduledPostPlatform) => {
      const connectKey = PLATFORM_CONNECT_CONFIG[platform].connectKey;
      if (!connectKey) return;
      const baseConnectUrl = CONNECT_URLS[connectKey];
      if (!baseConnectUrl) return;
      // W kontekscie agencyjnym token musi trafic pod klienta (forUserId), inaczej
      // integracja zapisze sie pod konto agencji, a scheduler pyta o klienta -> "niepolaczone".
      const connectUrl = activeClientUserId
        ? `${baseConnectUrl}?forUserId=${encodeURIComponent(activeClientUserId)}`
        : baseConnectUrl;
      await runOAuthPopup(connectUrl, `${platform}_oauth`);
    },
    [activeClientUserId, runOAuthPopup]
  );

  // Second way to connect Instagram: direct Instagram Business login (no Facebook Page).
  const openInstagramDirectConnect = useCallback(async () => {
    const baseUrl = "/api/integrations/instagram/connect";
    const connectUrl = activeClientUserId
      ? `${baseUrl}?forUserId=${encodeURIComponent(activeClientUserId)}`
      : baseUrl;
    await runOAuthPopup(connectUrl, "instagram_direct_oauth");
  }, [activeClientUserId, runOAuthPopup]);

  useEffect(() => {
    const formatOAuthErrorDetails = (rawError: unknown, eventType: string): string | null => {
      if (typeof rawError !== "string" || !rawError.trim()) {
        return null;
      }

      const normalizedError = rawError.toLowerCase();
      const platformNameByEvent: Record<string, string> = {
        META_OAUTH_ERROR: "Meta",
        YOUTUBE_OAUTH_ERROR: "YouTube",
        TIKTOK_OAUTH_ERROR: "TikTok",
        LINKEDIN_OAUTH_ERROR: "LinkedIn",
        INSTAGRAM_OAUTH_ERROR: "Instagram",
      };
      const platformName = platformNameByEvent[eventType] ?? "platformy";
      const knownErrorMessages: Record<string, string> = {
        "tiktok_client_key / tiktok_client_secret not set":
          "Brak konfiguracji TikTok OAuth na serwerze (TIKTOK_CLIENT_KEY / TIKTOK_CLIENT_SECRET).",
        "tiktok_client_key not set":
          "Brak konfiguracji TikTok OAuth na serwerze (TIKTOK_CLIENT_KEY).",
        "linkedin_client_id / linkedin_client_secret not set":
          "Brak konfiguracji LinkedIn OAuth na serwerze (LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET).",
        redirect_uri: `Redirect URI nie pasuje do konfiguracji aplikacji ${platformName}.`,
        invalid_scope: `Aplikacja ${platformName} nie ma wymaganych uprawnień (scope).`,
        oauth_denied: `Dostęp został odrzucony po stronie ${platformName}.`,
        access_denied: `Dostęp został odrzucony po stronie ${platformName}.`,
      };

      const matchedKey = Object.keys(knownErrorMessages).find((key) =>
        normalizedError.includes(key)
      );
      if (matchedKey) {
        return knownErrorMessages[matchedKey];
      }

      return rawError;
    };

    const successHandlers: Record<string, () => Promise<unknown>> = {
      META_OAUTH_SUCCESS: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] }),
          queryClient.invalidateQueries({ queryKey: ["/api/scheduler/pages"] }),
        ]),
      YOUTUBE_OAUTH_SUCCESS: () =>
        queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] }),
      TIKTOK_OAUTH_SUCCESS: () =>
        queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] }),
      INSTAGRAM_OAUTH_SUCCESS: () =>
        queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] }),
      LINKEDIN_OAUTH_SUCCESS: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] }),
          queryClient.invalidateQueries({ queryKey: ["/api/scheduler/linkedin-organizations"] }),
        ]),
    };

    const errorMessages: Record<string, string> = {
      META_OAUTH_ERROR: t("contentScheduler.oauth.connectMeta"),
      YOUTUBE_OAUTH_ERROR: t("contentScheduler.oauth.connectYoutube"),
      TIKTOK_OAUTH_ERROR: t("contentScheduler.oauth.connectTiktok"),
      LINKEDIN_OAUTH_ERROR: t("contentScheduler.oauth.connectLinkedin"),
      INSTAGRAM_OAUTH_ERROR: t("contentScheduler.oauth.connectInstagram"),
    };

    const handleOAuthMessage = (event: MessageEvent): void => {
      if (!event.data || typeof event.data !== "object") return;
      const eventData = event.data as { type?: string; error?: unknown };
      const eventType = eventData.type;
      if (!eventType) return;

      const successHandler = successHandlers[eventType];
      if (successHandler) {
        successHandler();
        toast({
          title: t("contentScheduler.oauth.integrationConnected"),
          description: t("contentScheduler.oauth.statusRefreshed"),
        });
        return;
      }

      const errorDescription = errorMessages[eventType];
      if (errorDescription) {
        const formattedErrorDetails = formatOAuthErrorDetails(eventData.error, eventType);
        const toastDescription = formattedErrorDetails
          ? `${errorDescription} ${formattedErrorDetails}`
          : errorDescription;

        toast({
          title: t("contentScheduler.oauth.connectPlatform"),
          description: toastDescription,
          variant: "destructive",
        });
      }
    };

    window.addEventListener("message", handleOAuthMessage);
    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [queryClient, toast, t]);

  // Posts by calendar slot
  const postsBySlot = useMemo(() => {
    const map = new Map<string, ScheduledPost[]>();
    for (const p of filteredPosts) {
      const dt = getPostCalendarDate(p);
      if (!dt) continue;
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}-${dt.getHours()}`;
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [filteredPosts]);

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; body: Record<string, unknown> }) => {
      const body = activeClientUserId ? { ...data.body, forUserId: activeClientUserId } : data.body;
      return data.id
        ? apiRequest("PUT", `/api/scheduler/${data.id}`, body)
        : apiRequest("POST", "/api/scheduler", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      closeModal();
      toast({
        title: editingId
          ? t("contentScheduler.toasts.postUpdated")
          : t("contentScheduler.toasts.postCreated"),
      });
    },
    onError: () =>
      toast({
        title: t("contentScheduler.toasts.tryAgain"),
        description: t("contentScheduler.toasts.tryAgainDesc"),
        variant: "destructive",
      }),
  });

  const formatErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
      return error.message.replace(/^\d+:\s*/, "");
    }
    if (typeof error === "string" && error.trim()) {
      return error;
    }
    return "Kliknij ponownie żeby spróbować jeszcze raz";
  };

  const publishMutation = useMutation({
    mutationFn: (postId: string) =>
      apiRequest("POST", withForUser(`/api/scheduler/${postId}/publish`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({ title: t("contentScheduler.toasts.postPublished") });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({
        title: t("contentScheduler.toasts.tryPublishAgain"),
        description: formatErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => apiRequest("DELETE", withForUser(`/api/scheduler/${postId}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({ title: t("contentScheduler.toasts.postDeleted") });
    },
  });

  const deleteBatchMutation = useMutation({
    mutationFn: (batchId: string) =>
      apiRequest("DELETE", withForUser(`/api/scheduler/bulk/${batchId}`)),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
      toast({ title: "Batch anulowany", description: `Usunięto ${data?.deleted ?? 0} postów.` });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Nie udało się anulować batcha";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    },
  });

  function handleDeleteBatch(batchId: string, count: number) {
    if (typeof window !== "undefined" && !window.confirm(`Anulować cały batch (${count} postów)?`))
      return;
    deleteBatchMutation.mutate(batchId);
  }

  function handleEditBatch(batchId: string) {
    setEditBatchId(batchId);
    setShowBulkModal(true);
  }

  const editBatchPosts = useMemo(() => {
    if (!editBatchId) return null;
    return posts.filter((p) => (p as { batchId?: string | null }).batchId === editBatchId);
  }, [editBatchId, posts]);

  // Form handlers
  function cleanupTemporaryMedia(items: TemporaryMediaItem[]) {
    for (const item of items) {
      if (item.source === "local" && item.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
    }
  }

  function closeModal() {
    if (temporaryMedia.some((m) => m.uploading)) {
      const ok =
        typeof window !== "undefined"
          ? window.confirm("Trwa upload pliku. Zamknąć mimo to? (upload zostanie przerwany)")
          : true;
      if (!ok) return;
    }
    cleanupTemporaryMedia(temporaryMedia);
    setTemporaryMedia([]);
    setForm(EMPTY_FORM);
    setEditingId(null);
    setEditingPostStatus("draft");
    setShowCreateModal(false);
  }

  function openCreateModal(
    presetDate?: Date,
    presetMedia?: { url: string; mediaType: "image" | "video"; originalName?: string }
  ) {
    const scheduledAt = presetDate
      ? toLocalDatetimeValue(presetDate)
      : toLocalDatetimeValue(new Date());
    if (presetMedia) {
      const defaultPlatform = resolveDefaultComposerPlatform();
      const defaultPostType: ScheduledMetaPostType = "post";
      setForm({
        ...EMPTY_FORM,
        platform: defaultPlatform ?? bestTimePlatform,
        postType: defaultPostType,
        platformPostTypes: defaultPlatform ? { [defaultPlatform]: defaultPostType } : {},
        scheduledAt,
        mediaType: presetMedia.mediaType,
        mediaUrls: [presetMedia.url],
      });
    } else {
      setForm({ ...EMPTY_FORM, scheduledAt });
    }
    setTemporaryMedia([]);
    setEditingId(null);
    setEditingPostStatus("draft");
    setModalSessionKey((key) => key + 1);
    setShowCreateModal(true);
  }

  function openEditModal(post: ScheduledPost) {
    const platform = post.platform as ScheduledPostPlatform;
    const postType = (post.postType as ScheduledMetaPostType) || "post";
    setForm({
      platform,
      postType,
      platformPostTypes: { [platform]: postType },
      title: post.title || "",
      caption: post.caption || "",
      mediaType: (post.mediaType || "text") as ScheduledPostMediaType,
      mediaUrls: (post.mediaUrls as string[]) || [],
      pageId: post.pageId || "",
      pageName: post.pageName || "",
      igAccountId: post.igAccountId || "",
      igAccountName: post.igAccountName || "",
      scheduledAt: post.scheduledAt ? toLocalDatetimeValue(new Date(post.scheduledAt)) : "",
      presets:
        (post as ScheduledPost & { presets?: SchedulerPresets | null }).presets ??
        DEFAULT_SCHEDULER_PRESETS,
      threadsSelected: false,
    });
    setTemporaryMedia([]);
    setEditingId(post.id);
    setEditingPostStatus(post.status);
    setModalSessionKey((key) => key + 1);
    setShowCreateModal(true);
  }

  function handlePageSelect(pageId: string) {
    const page = pages.find((p) => p.id === pageId);
    if (!page) return;
    setForm((prev) => ({
      ...prev,
      pageId: page.id,
      pageName: page.name,
      igAccountId: page.instagramBusinessAccount?.id || "",
      igAccountName:
        page.instagramBusinessAccount?.username || page.instagramBusinessAccount?.name || "",
    }));
  }

  function handleLinkedInTargetSelect(targetId: string) {
    if (!targetId || targetId === "person") {
      setForm((prev) => ({
        ...prev,
        pageId: "person",
        pageName: linkedInProfileName || "Twój profil",
        igAccountId: "",
        igAccountName: "",
      }));
      return;
    }

    const organization = linkedInOrganizations.find((item) => item.id === targetId);
    if (!organization) return;

    setForm((prev) => ({
      ...prev,
      pageId: organization.id,
      pageName: organization.name,
      igAccountId: "",
      igAccountName: "",
    }));
  }

  function addTemporaryMedia(items: TemporaryMediaItem[]) {
    setTemporaryMedia((prev) => [...prev, ...items]);
  }

  async function handleLocalMediaSelect(files: FileList) {
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const mediaType = inferTemporaryMediaType(file.type);
      const tempId = `${file.name}-${file.size}-${file.lastModified}`;
      const blobUrl = URL.createObjectURL(file);

      setTemporaryMedia((prev) => [
        ...prev,
        {
          id: tempId,
          source: "local" as const,
          name: file.name,
          mediaType,
          previewUrl: blobUrl,
          sizeLabel: formatBytes(file.size),
          uploading: true,
          progress: 0,
        },
      ]);

      try {
        const data = await uploadSchedulerMedia({
          file,
          forUserId: activeClientUserId ?? undefined,
          onProgress: (percent) => {
            setTemporaryMedia((prev) =>
              prev.map((item) => (item.id === tempId ? { ...item, progress: percent } : item))
            );
          },
        });
        URL.revokeObjectURL(blobUrl);
        setTemporaryMedia((prev) => prev.filter((item) => item.id !== tempId));
        setForm((prev) => {
          const mediaUrls = [...prev.mediaUrls, data.url];
          return { ...prev, mediaUrls, mediaType: deriveMediaType(mediaUrls) };
        });
      } catch (err) {
        URL.revokeObjectURL(blobUrl);
        const msg = err instanceof Error ? err.message : t("contentScheduler.toasts.uploadError");
        setTemporaryMedia((prev) =>
          prev.map((item) =>
            item.id === tempId ? { ...item, uploading: false, uploadError: msg } : item
          )
        );
        toast({
          title: t("contentScheduler.toasts.uploadError"),
          description: `${file.name}: ${msg}`,
          variant: "destructive",
        });
      }
    }
  }

  async function importCloudMedia(
    file: SelectedCloudFile
  ): Promise<{ url: string; mediaType: "image" | "video"; originalName: string }> {
    const imported = (await apiRequest(
      "POST",
      `/api/integrations/cloud-storage/import/${file.provider}`,
      { fileId: file.fileId }
    )) as CloudImportResponse;

    return {
      url: imported.path,
      mediaType: imported.mimeType?.startsWith("video/") ? "video" : "image",
      originalName: imported.originalName,
    };
  }

  async function handleCloudMediaSelect(file: SelectedCloudFile) {
    const tempId = `${file.provider}-${file.fileId}`;
    addTemporaryMedia([
      {
        id: tempId,
        source: "cloud",
        provider: file.provider,
        cloudFileId: file.fileId,
        name: file.originalName,
        mediaType: inferTemporaryMediaType(file.mimeType),
        previewUrl: file.thumbnailUrl,
        sizeLabel: formatBytes(file.size),
        uploading: true,
      },
    ]);

    try {
      const imported = await importCloudMedia(file);
      setTemporaryMedia((prev) => prev.filter((item) => item.id !== tempId));
      setForm((prev) => {
        const mediaUrls = [...prev.mediaUrls, imported.url];
        return { ...prev, mediaUrls, mediaType: deriveMediaType(mediaUrls) };
      });
      toast({
        title: t("contentScheduler.toasts.fileImported"),
        description: imported.originalName,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("contentScheduler.toasts.importFailed");
      setTemporaryMedia((prev) =>
        prev.map((item) =>
          item.id === tempId ? { ...item, uploading: false, uploadError: msg } : item
        )
      );
      toast({
        title: t("contentScheduler.toasts.importFailed"),
        description: msg,
        variant: "destructive",
      });
    }
  }

  async function handleDriveFileDrop(file: SelectedCloudFile, slotDate: Date) {
    setDriveImportingFileId(file.fileId);
    try {
      const imported = await importCloudMedia(file);
      openCreateModal(slotDate, imported);
      toast({
        title: t("contentScheduler.toasts.driveFileAdded"),
        description: imported.originalName,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("contentScheduler.toasts.driveAddFailed");
      toast({
        title: t("contentScheduler.toasts.driveAddFailed"),
        description: msg,
        variant: "destructive",
      });
    } finally {
      setDriveImportingFileId(null);
    }
  }

  function handleRemoveTemporaryMedia(id: string) {
    setTemporaryMedia((prev) => {
      const toRemove = prev.find((item) => item.id === id);
      if (toRemove?.source === "local" && toRemove.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(toRemove.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  }

  function toISOScheduledAt(value: string): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  function buildPostPayload(
    platform: ScheduledPostPlatform,
    postType: ScheduledMetaPostType,
    status: "draft" | "scheduled",
    scheduledAt?: string
  ): Record<string, unknown> {
    const { scheduledAt: _scheduledAt, platformPostTypes: _platformPostTypes, ...payload } = form;
    return {
      ...payload,
      platform,
      postType,
      status,
      ...(scheduledAt ? { scheduledAt } : {}),
    };
  }

  // Waliduje media pod limity karuzeli (IG 2–10, brak miksu wideo+zdjęcia) zanim
  // wyślemy post. Zwraca false + toast, jeśli coś jest nie tak.
  function validateMediaBeforeSubmit(): boolean {
    const urls = form.mediaUrls;
    if (urls.length < 2) return true;

    const hasVideo = urls.some(isVideoMediaUrl);
    const hasImage = urls.some((u) => !isVideoMediaUrl(u));
    if (hasVideo && hasImage) {
      toast({
        title: "Nie mieszaj wideo i zdjęć",
        description: "W jednym poście zostaw same zdjęcia (karuzela) albo jedno wideo.",
        variant: "destructive",
      });
      return false;
    }
    if (hasVideo) {
      toast({
        title: "Tylko jedno wideo",
        description: "Na jeden post możesz dodać jedno wideo. Usuń nadmiarowe pliki.",
        variant: "destructive",
      });
      return false;
    }

    const platforms = editingId
      ? [form.platform]
      : (Object.keys(form.platformPostTypes) as ScheduledPostPlatform[]);
    if (platforms.includes("instagram") && urls.length > 10) {
      toast({
        title: "Za dużo zdjęć w karuzeli",
        description: `Instagram obsługuje maksymalnie 10 zdjęć w karuzeli (masz ${urls.length}).`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  }

  function handleSchedule() {
    const scheduledAt = toISOScheduledAt(form.scheduledAt);
    if (!scheduledAt) {
      toast({
        title: "Wybierz termin",
        description: "Ustaw datę i godzinę publikacji.",
        variant: "destructive",
      });
      return;
    }
    if (!validateMediaBeforeSubmit()) return;
    if (editingId) {
      saveMutation.mutate({
        id: editingId,
        body: buildPostPayload(form.platform, form.postType, "scheduled", scheduledAt),
      });
      return;
    }
    const selectedEntries = Object.entries(form.platformPostTypes) as [
      ScheduledPostPlatform,
      ScheduledMetaPostType,
    ][];
    for (const [platform, postType] of selectedEntries) {
      saveMutation.mutate({
        id: undefined,
        body: buildPostPayload(platform, postType, "scheduled", scheduledAt),
      });
    }
  }

  function handleSaveDraft() {
    if (editingId) {
      saveMutation.mutate({
        id: editingId,
        body: buildPostPayload(form.platform, form.postType, "draft"),
      });
      return;
    }
    const selectedEntries = Object.entries(form.platformPostTypes) as [
      ScheduledPostPlatform,
      ScheduledMetaPostType,
    ][];
    for (const [platform, postType] of selectedEntries) {
      saveMutation.mutate({ id: undefined, body: buildPostPayload(platform, postType, "draft") });
    }
  }

  async function handlePublishNow() {
    if (!validateMediaBeforeSubmit()) return;
    const selectedEntries = Object.entries(form.platformPostTypes) as [
      ScheduledPostPlatform,
      ScheduledMetaPostType,
    ][];
    const targets = editingId
      ? [
          {
            id: editingId,
            platform: form.platform as ScheduledPostPlatform,
            postType: form.postType,
          },
        ]
      : selectedEntries.map(([platform, postType]) => ({
          id: undefined as string | undefined,
          platform,
          postType,
        }));

    let anyFailed = false;
    for (const target of targets) {
      try {
        let postId = target.id;
        if (!postId) {
          const saved = (await apiRequest(
            "POST",
            "/api/scheduler",
            buildPostPayload(target.platform, target.postType, "draft")
          )) as SchedulerCreatePostResponse;
          postId = saved.post.id as string;
        } else {
          await apiRequest(
            "PUT",
            `/api/scheduler/${postId}`,
            buildPostPayload(target.platform, target.postType, "draft")
          );
        }
        await apiRequest("POST", `/api/scheduler/${postId}/publish`);
      } catch (err) {
        anyFailed = true;
        toast({
          title: t("contentScheduler.toasts.tryPublishAgain"),
          description: formatErrorMessage(err),
          variant: "destructive",
        });
      }
    }
    queryClient.invalidateQueries({ queryKey: ["/api/scheduler"] });
    const publishedToTiktok = targets.some((target) => target.platform === "tiktok");
    closeModal();
    if (!anyFailed) {
      toast({
        title: t("contentScheduler.toasts.published"),
        // TikTok guideline 5d: notify the user that processing is async and may
        // take a few minutes to appear on their profile.
        description: publishedToTiktok ? t("contentScheduler.toasts.tiktokProcessing") : undefined,
      });
    }
  }

  function handleDuplicate() {
    // Capture form BEFORE closeModal() resets it
    const snapshot = { ...form, scheduledAt: "" };
    closeModal();
    setEditingId(null);
    setForm(snapshot);
    setShowCreateModal(true);
    toast({
      title: t("contentScheduler.toasts.duplicated"),
      description: t("contentScheduler.toasts.duplicatedDesc"),
    });
  }

  useEffect(() => {
    temporaryMediaRef.current = temporaryMedia;
  }, [temporaryMedia]);

  useEffect(() => () => cleanupTemporaryMedia(temporaryMediaRef.current), []);

  // ─── Render ───
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="px-6 py-4 border-b border-white/[0.06] shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex bg-white/[0.04] rounded-lg p-0.5">
              <ViewToggleButton
                active={viewMode === "calendar"}
                onClick={() => setViewMode("calendar")}
                icon={<CalendarDays size={13} />}
                label={t("contentScheduler.calendarView")}
              />
              <ViewToggleButton
                active={viewMode === "list"}
                onClick={() => setViewMode("list")}
                icon={<List size={13} />}
                label={t("contentScheduler.listView")}
              />
            </div>
            {viewMode === "calendar" && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setWeekOffset(0)}
                  className={`px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-colors ${
                    weekOffset === 0
                      ? "text-[#ef3e29] bg-[#ef3e29]/10 hover:bg-[#ef3e29]/15"
                      : "text-white/50 bg-white/[0.04] hover:bg-white/[0.08]"
                  }`}
                >
                  {t("contentScheduler.today")}
                </button>
                <button
                  onClick={() => setWeekOffset((o) => o - 1)}
                  className="p-1.5 text-white/40 hover:text-white/70 rounded-lg hover:bg-white/[0.04]"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setWeekOffset((o) => o + 1)}
                  className="p-1.5 text-white/40 hover:text-white/70 rounded-lg hover:bg-white/[0.04]"
                >
                  <ChevronRight size={14} />
                </button>
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 text-xs text-white/60 font-medium ml-1 px-2.5 py-1.5 rounded-lg border border-white/[0.07] hover:bg-white/[0.06] hover:text-white/80 hover:border-white/[0.13] transition-colors">
                      <CalendarDays size={12} className="text-white/30 shrink-0" />
                      {formatWeekRange(
                        currentWeekStart,
                        t("contentScheduler.monthNames", { returnObjects: true }) as string[]
                      )}
                      <ChevronDown size={10} className="text-white/25 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-popover border-white/[0.08] shadow-2xl shadow-black/60"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      defaultMonth={currentWeekStart}
                      selected={currentWeekStart}
                      onSelect={(date) => {
                        if (!date) return;
                        const trueWeekStart = getWeekStart(new Date());
                        const selectedWeekStart = getWeekStart(date);
                        const diffWeeks = Math.round(
                          (selectedWeekStart.getTime() - trueWeekStart.getTime()) /
                            (7 * 24 * 60 * 60 * 1000)
                        );
                        setWeekOffset(diffWeeks);
                        setShowDatePicker(false);
                      }}
                      weekStartsOn={1}
                      modifiers={{ week: weekDays }}
                      modifiersStyles={{ week: { backgroundColor: "rgba(239,62,41,0.08)" } }}
                      classNames={{
                        months: "flex flex-col",
                        month: "space-y-3 p-3",
                        caption: "flex justify-center pt-1 relative items-center",
                        caption_label: "text-xs font-semibold text-white/80",
                        nav: "space-x-1 flex items-center",
                        nav_button:
                          "h-6 w-6 bg-transparent p-0 text-white/40 hover:text-white/80 hover:bg-white/[0.06] rounded flex items-center justify-center",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "flex",
                        head_cell: "text-white/25 w-9 font-normal text-[10px] text-center pb-1",
                        row: "flex w-full mt-0.5",
                        cell: "h-8 w-9 text-center text-sm p-0 relative",
                        day: "h-8 w-9 p-0 font-normal text-xs text-white/60 hover:bg-white/[0.08] hover:text-white rounded-md transition-colors cursor-pointer",
                        day_selected:
                          "!bg-[#ef3e29] !text-white hover:!bg-[#ef3e29] hover:!text-white font-medium",
                        day_today: "!text-[#ef3e29] font-semibold",
                        day_outside: "text-white/20 opacity-50",
                        day_disabled: "text-white/15 opacity-30",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDrivePanelOpen((prev) => !prev)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                drivePanelOpen
                  ? "bg-emerald-500/12 border-emerald-400/25 text-emerald-100"
                  : "bg-white/[0.04] border-white/[0.08] text-white/65 hover:bg-white/[0.08] hover:text-white"
              }`}
              title="Google Drive"
            >
              <HardDrive size={14} /> {t("contentScheduler.driveButton")}
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg text-sm font-medium transition-colors border border-purple-500/30"
              title="Plan zbiorczy (bulk)"
              data-testid="bulk-schedule-button"
            >
              <Plus size={14} /> Bulk
            </button>
            <button
              onClick={() => openCreateModal()}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#ef3e29] text-white rounded-lg text-sm font-medium hover:bg-[#d63520] transition-colors shadow-lg shadow-[#ef3e29]/20"
            >
              <Plus size={14} /> {t("contentScheduler.createPost")}
            </button>
          </div>
        </div>

        <PlannerToolbar
          filters={filters}
          activeFilterCount={
            (filters.platforms.length !== ALL_SCHEDULER_PLATFORMS.length ? 1 : 0) +
            (filters.statuses.length !== ALL_SCHEDULER_STATUSES.length ? 1 : 0) +
            (filters.search ? 1 : 0)
          }
          showFilters={showFilters}
          showBestTimes={showBestTimes}
          showBestTimePercent={showBestTimePercent}
          bestTimePlatform={bestTimePlatform}
          bestTimePlatformOptions={bestTimePlatformOptions}
          bestTimeSource={bestTimeSource}
          onSearchChange={(value) => setFilters((prev) => ({ ...prev, search: value }))}
          onToggleFilters={() => setShowFilters((prev) => !prev)}
          onToggleBestTimes={() => setShowBestTimes((prev) => !prev)}
          onShowBestTimePercentChange={setShowBestTimePercent}
          onBestTimePlatformChange={(platform) => {
            bestTimePlatformTouchedRef.current = true;
            setBestTimePlatform(platform);
            setShowBestTimes(true);
          }}
        />

        {showFilters && (
          <PlannerFiltersPanel
            filters={filters}
            onClear={() =>
              setFilters({
                search: "",
                platforms: [...ALL_SCHEDULER_PLATFORMS],
                statuses: [...ALL_SCHEDULER_STATUSES],
              })
            }
            onTogglePlatform={(platform) =>
              setFilters((prev) => ({
                ...prev,
                platforms: prev.platforms.includes(platform)
                  ? prev.platforms.filter((item) => item !== platform)
                  : [...prev.platforms, platform],
              }))
            }
            onToggleStatus={(status) =>
              setFilters((prev) => ({
                ...prev,
                statuses: prev.statuses.includes(status)
                  ? prev.statuses.filter((item) => item !== status)
                  : [...prev.statuses, status],
              }))
            }
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 flex overflow-hidden bg-background">
        <div className="flex-1 overflow-auto">
          {viewMode === "calendar" ? (
            <WeekCalendar
              weekDays={weekDays}
              today={today}
              postsBySlot={postsBySlot}
              onSlotClick={openCreateModal}
              onPostClick={openEditModal}
              postsLoading={postsLoading}
              bestTimeLookup={bestTimeLookup}
              bestTimePlatform={bestTimePlatform}
              showBestTimes={showBestTimes}
              showBestTimePercent={showBestTimePercent}
              onDriveFileDrop={handleDriveFileDrop}
            />
          ) : (
            <PostListView
              posts={filteredPosts}
              postsLoading={postsLoading}
              onEdit={openEditModal}
              onPublish={(id) => publishMutation.mutate(id)}
              onDelete={(id) => deleteMutation.mutate(id)}
              onDeleteBatch={handleDeleteBatch}
              onEditBatch={handleEditBatch}
              isPublishing={publishMutation.isPending}
            />
          )}
        </div>
        {drivePanelOpen && (
          <SchedulerDrivePanel
            importingFileId={driveImportingFileId}
            onClose={() => setDrivePanelOpen(false)}
          />
        )}
      </div>

      {/* Create/Edit modal */}
      <Dialog
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
      >
        <DialogContent className="max-w-[1100px] w-[95vw] h-[85vh] max-h-[750px] p-0 bg-background border-white/[0.08] overflow-hidden flex flex-col [&>button:last-child]:hidden">
          <CreatePostModal
            key={modalSessionKey}
            form={form}
            setForm={setForm}
            pages={pages}
            pagesLoading={pagesLoading}
            linkedInOrganizations={linkedInOrganizations}
            linkedInOrganizationsLoading={linkedInOrganizationsLoading}
            linkedInProfileName={linkedInProfileName}
            connectedPlatforms={connectedPlatforms}
            connectedPlatformDetails={connectedPlatformsData?.platforms ?? []}
            connectablePlatforms={connectablePlatforms}
            onConnectPlatform={openOAuthConnect}
            onConnectInstagramDirect={openInstagramDirectConnect}
            onPageSelect={handlePageSelect}
            onLinkedInTargetSelect={handleLinkedInTargetSelect}
            onRemoveMediaUrl={(i) =>
              setForm((prev) => {
                const mediaUrls = prev.mediaUrls.filter((_, idx) => idx !== i);
                return { ...prev, mediaUrls, mediaType: deriveMediaType(mediaUrls) };
              })
            }
            onReorderMediaUrls={(mediaUrls) =>
              setForm((prev) => ({ ...prev, mediaUrls, mediaType: deriveMediaType(mediaUrls) }))
            }
            temporaryMedia={temporaryMedia}
            onLocalMediaSelect={handleLocalMediaSelect}
            onCloudMediaSelect={handleCloudMediaSelect}
            onRemoveTemporaryMedia={handleRemoveTemporaryMedia}
            onSchedule={handleSchedule}
            onSaveDraft={handleSaveDraft}
            onPublishNow={handlePublishNow}
            onDuplicate={handleDuplicate}
            onCancel={closeModal}
            isEditing={!!editingId}
            editingPostStatus={editingPostStatus}
            isSaving={saveMutation.isPending}
            forUserId={activeClientUserId ?? undefined}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk schedule dialog (create + edit) */}
      <BulkSchedulerDialog
        open={showBulkModal}
        onOpenChange={(open) => {
          setShowBulkModal(open);
          if (!open) setEditBatchId(null);
        }}
        pages={pages}
        linkedInOrganizations={linkedInOrganizations}
        connectedPlatforms={connectedPlatforms}
        forUserId={activeClientUserId ?? undefined}
        editBatch={
          editBatchId && editBatchPosts
            ? { batchId: editBatchId, posts: editBatchPosts as any }
            : undefined
        }
      />
    </div>
  );
}

// ─── Small helpers ───

function ViewToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${active ? "bg-white/[0.1] text-white" : "text-white/40 hover:text-white/60"}`}
    >
      {icon} {label}
    </button>
  );
}

function PlannerToolbar({
  filters,
  activeFilterCount,
  showFilters,
  showBestTimes,
  showBestTimePercent,
  bestTimePlatform,
  bestTimePlatformOptions,
  bestTimeSource,
  onSearchChange,
  onToggleFilters,
  onToggleBestTimes,
  onShowBestTimePercentChange,
  onBestTimePlatformChange,
}: {
  filters: PlannerFilters;
  activeFilterCount: number;
  showFilters: boolean;
  showBestTimes: boolean;
  showBestTimePercent: boolean;
  bestTimePlatform: ScheduledPostPlatform;
  bestTimePlatformOptions: ScheduledPostPlatform[];
  bestTimeSource: string;
  onSearchChange: (value: string) => void;
  onToggleFilters: () => void;
  onToggleBestTimes: () => void;
  onShowBestTimePercentChange: (value: boolean) => void;
  onBestTimePlatformChange: (platform: ScheduledPostPlatform) => void;
}) {
  const { t } = useTranslation();
  const [bestTimesMenuOpen, setBestTimesMenuOpen] = useState(false);
  const activePlatform =
    PLATFORM_OPTIONS.find((item) => item.id === bestTimePlatform) ?? PLATFORM_OPTIONS[0];
  const ActiveIcon = activePlatform.icon;
  const sourceLabel = bestTimeSource === "meta_insights" ? "Meta insights" : "Benchmark";

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
        <input
          value={filters.search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("contentScheduler.searchPlaceholder")}
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] py-2.5 pl-9 pr-4 text-sm text-white/80 placeholder:text-white/30 focus:outline-none focus:border-white/[0.16]"
        />
      </div>

      <button
        onClick={onToggleFilters}
        className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${showFilters ? "border-[#ef3e29]/40 bg-[#ef3e29]/10 text-white" : "border-white/[0.08] bg-white/[0.03] text-white/65 hover:bg-white/[0.06]"}`}
      >
        <SlidersHorizontal size={14} />
        {t("contentScheduler.filters")}
        {activeFilterCount > 0 && (
          <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-white/[0.12] px-1.5 py-0.5 text-[10px]">
            {activeFilterCount}
          </span>
        )}
      </button>

      <div className="relative">
        <button
          onClick={() => setBestTimesMenuOpen((prev) => !prev)}
          className={`inline-flex h-[42px] items-center gap-2 rounded-xl border px-3.5 text-sm transition-colors ${
            showBestTimes
              ? "border-blue-500/30 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
              : "border-white/[0.08] bg-white/[0.03] text-white/65 hover:bg-white/[0.06]"
          }`}
        >
          {showBestTimes ? (
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-white/[0.08] bg-white/[0.05]"
              style={{ boxShadow: `inset 2px 0 0 ${activePlatform.color}` }}
            >
              <ActiveIcon size={12} style={{ color: activePlatform.color }} />
            </span>
          ) : (
            <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-current text-[9px]">
              ×
            </span>
          )}
          Best times
          <ChevronDown
            size={13}
            className={
              bestTimesMenuOpen ? "rotate-180 transition-transform" : "transition-transform"
            }
          />
        </button>

        {bestTimesMenuOpen && (
          <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[190px] overflow-hidden rounded-xl border border-white/[0.12] bg-popover text-[11px] text-white/78 shadow-2xl shadow-black/35">
            <button
              onClick={() => {
                if (showBestTimes) onToggleBestTimes();
                setBestTimesMenuOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-white/[0.07]"
            >
              <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-white/25 text-[9px] text-white/55">
                ×
              </span>
              {t("contentScheduler.none")}
            </button>
            {bestTimePlatformOptions.map((platform) => {
              const option = PLATFORM_OPTIONS.find((item) => item.id === platform);
              const Icon = option?.icon ?? Facebook;
              const isActive = showBestTimes && bestTimePlatform === platform;
              return (
                <button
                  key={platform}
                  onClick={() => {
                    onBestTimePlatformChange(platform);
                    setBestTimesMenuOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-white/[0.07] ${isActive ? "bg-white/[0.09] text-white" : ""}`}
                >
                  <Icon size={13} style={{ color: option?.color ?? "#111827" }} />
                  <span className="flex-1">{option?.label ?? platform}</span>
                  {isActive && <Check size={12} className="text-white/85" />}
                </button>
              );
            })}
            <div className="mt-1 flex items-center justify-between border-t border-white/[0.08] px-3 py-2.5">
              <span className="text-white/68">{t("contentScheduler.showAsPercentage")}</span>
              <button
                type="button"
                role="switch"
                aria-checked={showBestTimePercent}
                aria-label="Show best times as percentage"
                onClick={() => onShowBestTimePercentChange(!showBestTimePercent)}
                className={`relative inline-flex h-[18px] w-[34px] shrink-0 items-center rounded-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#6d7dff]/30 ${
                  showBestTimePercent
                    ? "border-[#6d7dff] bg-[#6d7dff] shadow-[0_0_0_1px_rgba(109,125,255,0.25)]"
                    : "border-white/[0.12] bg-white/[0.09] hover:bg-white/[0.13]"
                }`}
              >
                <span
                  className={`h-[14px] w-[14px] rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.36)] transition-transform duration-200 ${
                    showBestTimePercent ? "translate-x-[17px]" : "translate-x-[2px]"
                  }`}
                />
              </button>
            </div>
            {showBestTimes && (
              <div className="border-t border-white/[0.08] px-3 py-2 text-[10px] text-white/38">
                {t("contentScheduler.source")} {sourceLabel}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PlannerFiltersPanel({
  filters,
  onClear,
  onTogglePlatform,
  onToggleStatus,
}: {
  filters: PlannerFilters;
  onClear: () => void;
  onTogglePlatform: (platform: ScheduledPostPlatform) => void;
  onToggleStatus: (status: ScheduleStatusFilter) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0d1118] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="text-sm text-white/75">{t("contentScheduler.filterPlanner")}</div>
        <button
          onClick={onClear}
          className="rounded-lg border border-white/[0.08] px-3 py-1.5 text-xs text-white/55 hover:bg-white/[0.05]"
        >
          {t("contentScheduler.none")}
        </button>
      </div>
      <div className="grid gap-0 md:grid-cols-2">
        <FilterSection title={t("contentScheduler.network")}>
          {ALL_SCHEDULER_PLATFORMS.map((platform) => {
            const option = PLATFORM_OPTIONS.find((item) => item.id === platform);
            const Icon = option?.icon ?? Facebook;
            const active = filters.platforms.includes(platform);
            return (
              <FilterToggle
                key={platform}
                active={active}
                onClick={() => onTogglePlatform(platform)}
                icon={<Icon size={14} style={{ color: option?.color }} />}
                label={option?.label || platform}
              />
            );
          })}
        </FilterSection>

        <FilterSection title={t("contentScheduler.publicationStatus")}>
          {ALL_SCHEDULER_STATUSES.map((status) => (
            <FilterToggle
              key={status}
              active={filters.statuses.includes(status)}
              onClick={() => onToggleStatus(status)}
              dotClass={STATUS_CONFIG[status]?.dotColor}
              label={t(STATUS_CONFIG[status]?.labelKey ?? status)}
            />
          ))}
        </FilterSection>
      </div>
    </div>
  );
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="p-4 border-t border-white/[0.04] first:border-t-0 md:border-r md:last:border-r-0 md:first:border-t-0">
      <div className="text-xs uppercase tracking-[0.2em] text-white/30 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FilterToggle({
  active,
  onClick,
  icon,
  label,
  dotClass,
}: {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
  dotClass?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between rounded-xl border px-3 py-2 text-sm transition-colors ${active ? "border-white/[0.16] bg-white/[0.06] text-white" : "border-white/[0.05] bg-transparent text-white/50 hover:bg-white/[0.04]"}`}
    >
      <span className="flex items-center gap-2">
        {dotClass ? <span className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass}`} /> : icon}
        {label}
      </span>
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-md border ${active ? "border-white/20 bg-white/12 text-white" : "border-white/10 text-transparent"}`}
      >
        <Check size={12} />
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// SchedulerDrivePanel
// ═══════════════════════════════════════════════════════

function SchedulerDrivePanel({
  importingFileId,
  onClose,
}: {
  importingFileId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [folderStack, setFolderStack] = useState<Array<{ id: string; name: string }>>([]);
  const [search, setSearch] = useState("");
  const currentFolderId =
    folderStack.length > 0 ? folderStack[folderStack.length - 1].id : undefined;

  const statusQuery = useQuery<SchedulerDriveStatusResponse>({
    queryKey: ["cloud-storage-status"],
    queryFn: () =>
      apiRequest(
        "GET",
        "/api/integrations/cloud-storage/status"
      ) as Promise<SchedulerDriveStatusResponse>,
    staleTime: 30_000,
    retry: false,
  });

  const connected = Boolean(statusQuery.data?.google_drive?.connected);

  const filesQuery = useQuery<{ files: SchedulerDriveFile[] }>({
    queryKey: ["cloud-storage-files", "google_drive", currentFolderId],
    queryFn: () => {
      const params = currentFolderId ? `?folderId=${encodeURIComponent(currentFolderId)}` : "";
      return apiRequest(
        "GET",
        `/api/integrations/cloud-storage/files/google_drive${params}`
      ) as Promise<{ files: SchedulerDriveFile[] }>;
    },
    enabled: connected,
    staleTime: 30_000,
    retry: false,
  });

  const files = useMemo(() => filesQuery.data?.files ?? [], [filesQuery.data?.files]);
  const visibleFiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return files;
    return files.filter((file) => file.name.toLowerCase().includes(query));
  }, [files, search]);

  const handleConnect = useCallback(async () => {
    const popup = window.open(
      "about:blank",
      "google_drive_oauth",
      "width=600,height=700,toolbar=no,menubar=no"
    );
    if (popup) {
      popup.document.write(
        `<html><head><title>${t("contentScheduler.drive.connectingTitle")}</title></head><body style="background:#101218;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>${t("contentScheduler.drive.connectingMessage")}</p></body></html>`
      );
      popup.document.close();
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== "cloud_storage_callback") return;
      window.removeEventListener("message", handleMessage);
      queryClient.invalidateQueries({ queryKey: ["cloud-storage-status"] });
      queryClient.invalidateQueries({ queryKey: ["hub-cloud-storage"] });
      if (event.data?.success) {
        setFolderStack([]);
      }
    };

    window.addEventListener("message", handleMessage);

    try {
      const data = (await apiRequest(
        "GET",
        "/api/integrations/cloud-storage/connect/google_drive"
      )) as { url?: string };
      if (data.url && popup && !popup.closed) {
        popup.location.href = data.url;
      }
    } catch {
      window.removeEventListener("message", handleMessage);
      if (popup && !popup.closed) {
        popup.close();
      }
    }
  }, [queryClient, t]);

  return (
    <aside className="w-[360px] shrink-0 border-l border-white/[0.08] bg-[#0b0f16] flex flex-col shadow-[-18px_0_40px_rgba(0,0,0,0.25)]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
        <div className="flex items-center gap-2 min-w-0">
          <HardDrive size={16} className="text-emerald-300" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">
              {t("contentScheduler.drive.title")}
            </div>
            {statusQuery.data?.google_drive?.email && (
              <div className="text-[10px] text-white/35 truncate">
                {statusQuery.data.google_drive.email}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-white/35 hover:text-white/70 hover:bg-white/[0.06]"
          title={t("contentScheduler.drive.close")}
        >
          <X size={15} />
        </button>
      </div>

      {!connected ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 flex items-center justify-center mb-4">
            <HardDrive size={22} className="text-emerald-300" />
          </div>
          <div className="text-sm font-medium text-white">
            {t("contentScheduler.drive.connect")}
          </div>
          <p className="mt-2 text-xs text-white/42 leading-relaxed">
            {t("contentScheduler.drive.connectDescription")}
          </p>
          <button
            onClick={handleConnect}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-[#06110b] hover:bg-emerald-400 transition-colors"
          >
            <Plug size={14} />
            {t("contentScheduler.drive.connectButton")}
          </button>
        </div>
      ) : (
        <>
          <div className="p-3 border-b border-white/[0.06] space-y-3">
            <div className="relative">
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/28"
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("contentScheduler.drive.searchPlaceholder")}
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.04] py-2 pl-8 pr-3 text-xs text-white/75 placeholder:text-white/30 focus:outline-none focus:border-emerald-400/35"
              />
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-white/45 min-w-0">
              <button
                onClick={() => setFolderStack([])}
                className="p-1 rounded hover:bg-white/[0.06] hover:text-white/70"
                title="Root"
              >
                <Home size={12} />
              </button>
              {folderStack.map((folder, index) => (
                <button
                  key={folder.id}
                  onClick={() => setFolderStack((prev) => prev.slice(0, index + 1))}
                  className="truncate rounded px-1.5 py-1 hover:bg-white/[0.06] hover:text-white/70"
                  title={folder.name}
                >
                  / {folder.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {filesQuery.isLoading ? (
              // VP-208: placeholder listy plików Drive zamiast spinnera.
              <ListSkeleton rows={6} withAvatar={false} />
            ) : filesQuery.error ? (
              <div className="py-12 text-center">
                <AlertCircle size={20} className="mx-auto mb-2 text-red-300/70" />
                <p className="text-xs text-white/45">{t("contentScheduler.drive.loadError")}</p>
                <button
                  onClick={() => filesQuery.refetch()}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] px-3 py-1.5 text-xs text-white/60 hover:bg-white/[0.05]"
                >
                  <RefreshCw size={12} />
                  {t("contentScheduler.drive.refresh")}
                </button>
              </div>
            ) : visibleFiles.length === 0 ? (
              <div className="py-16 text-center">
                <Cloud size={28} className="mx-auto mb-3 text-white/15" />
                <p className="text-xs text-white/38">{t("contentScheduler.drive.emptyFolder")}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {visibleFiles.map((file) => (
                  <SchedulerDriveFileRow
                    key={file.id}
                    file={file}
                    isImporting={importingFileId === file.id}
                    onOpenFolder={() =>
                      setFolderStack((prev) => [...prev, { id: file.id, name: file.name }])
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/[0.08] px-3 py-3 text-[11px] text-white/38">
            {t("contentScheduler.drive.dragHint")}
          </div>
        </>
      )}
    </aside>
  );
}

function SchedulerDriveFileRow({
  file,
  isImporting,
  onOpenFolder,
}: {
  file: SchedulerDriveFile;
  isImporting: boolean;
  onOpenFolder: () => void;
}) {
  const { t } = useTranslation();
  const isVideo = file.mimeType.startsWith("video/");
  const selectedFile: SelectedCloudFile = {
    provider: "google_drive",
    fileId: file.id,
    originalName: file.name,
    mimeType: file.mimeType,
    size: file.size,
    modifiedAt: file.modifiedAt,
    thumbnailUrl: file.thumbnailUrl,
  };

  if (file.isFolder) {
    return (
      <button
        type="button"
        onClick={onOpenFolder}
        className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-white/72 hover:bg-white/[0.05] transition-colors"
      >
        <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-400/15 flex items-center justify-center shrink-0">
          <FolderOpen size={18} className="text-amber-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">{file.name}</div>
          <div className="text-[10px] text-white/30">{t("contentScheduler.folder")}</div>
        </div>
        <ChevronRight size={13} className="text-white/25" />
      </button>
    );
  }

  return (
    <div
      draggable={!isImporting}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData(DRIVE_DRAG_MIME, JSON.stringify(selectedFile));
      }}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
        isImporting
          ? "bg-emerald-500/[0.08] opacity-70"
          : "cursor-grab hover:bg-white/[0.05] active:cursor-grabbing"
      }`}
      title={t("contentScheduler.dragToCalendar")}
    >
      <div className="h-10 w-10 rounded-lg bg-white/[0.05] border border-white/[0.08] overflow-hidden flex items-center justify-center shrink-0">
        {file.thumbnailUrl ? (
          <img src={file.thumbnailUrl} alt="" className="h-full w-full object-cover" />
        ) : isVideo ? (
          <Film size={17} className="text-purple-300" />
        ) : (
          <ImagePlus size={17} className="text-emerald-300" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-white/78">{file.name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-white/30">
          {isVideo ? <Video size={10} /> : <ImagePlus size={10} />}
          <span>{formatBytes(file.size)}</span>
        </div>
      </div>
      {isImporting ? (
        <Loader2 size={14} className="animate-spin text-emerald-300" />
      ) : (
        <HardDrive
          size={13}
          className="text-white/20 group-hover:text-emerald-300/70 transition-colors"
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// WeekCalendar
// ═══════════════════════════════════════════════════════

function WeekCalendar({
  weekDays,
  today,
  postsBySlot,
  onSlotClick,
  onPostClick,
  postsLoading,
  bestTimeLookup,
  bestTimePlatform,
  showBestTimes,
  showBestTimePercent,
  onDriveFileDrop,
}: {
  weekDays: Date[];
  today: Date;
  postsBySlot: Map<string, ScheduledPost[]>;
  onSlotClick: (d: Date) => void;
  onPostClick: (p: ScheduledPost) => void;
  postsLoading: boolean;
  bestTimeLookup: Map<string, SchedulerBestTimeSlot>;
  bestTimePlatform: ScheduledPostPlatform;
  showBestTimes: boolean;
  showBestTimePercent: boolean;
  onDriveFileDrop: (file: SelectedCloudFile, slotDate: Date) => void;
}) {
  const { t } = useTranslation();
  const dayNames = t("contentScheduler.dayNames", { returnObjects: true }) as string[];
  if (postsLoading)
    return (
      <div className="flex items-center justify-center h-full text-white/30">
        <Loader2 className="animate-spin mr-2" size={16} /> {t("contentScheduler.loading")}
      </div>
    );

  return (
    <div className="min-h-full px-6 py-4">
      {/* Day headers */}
      <div className="grid grid-cols-[56px_repeat(7,1fr)] sticky top-0 z-10 bg-card backdrop-blur-sm border border-white/[0.12] mb-2 rounded-t-lg overflow-hidden">
        <div className="p-2" />
        {weekDays.map((day, i) => {
          const isToday = isSameDay(day, today);
          return (
            <div
              key={i}
              className={`py-3 px-2 text-center border-l border-white/[0.14] ${isToday ? "bg-[#ef3e29]/12" : "bg-muted"}`}
            >
              <div
                className={`text-[10px] uppercase tracking-wider ${isToday ? "text-[#ff6b57]" : "text-white/55"}`}
              >
                {dayNames[day.getDay()]}
              </div>
              <div
                className={`text-lg font-semibold ${isToday ? "text-[#ff6b57]" : "text-white/90"}`}
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      {/* Time grid */}
      <div className="grid grid-cols-[56px_repeat(7,1fr)] bg-card rounded-b-lg border border-white/[0.12] border-t-0 overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
        {HOURS.map((hour) => (
          <TimeRow
            key={hour}
            hour={hour}
            weekDays={weekDays}
            today={today}
            postsBySlot={postsBySlot}
            onSlotClick={onSlotClick}
            onPostClick={onPostClick}
            bestTimeLookup={bestTimeLookup}
            bestTimePlatform={bestTimePlatform}
            showBestTimes={showBestTimes}
            showBestTimePercent={showBestTimePercent}
            onDriveFileDrop={onDriveFileDrop}
          />
        ))}
      </div>
    </div>
  );
}

// Status priority for grouping: most "severe" status wins for chip color
const STATUS_PRIORITY: Record<string, number> = {
  failed: 5,
  publishing: 4,
  scheduled: 3,
  published: 2,
  draft: 1,
};

function groupPostsByMinute(posts: ScheduledPost[]): Map<number, ScheduledPost[]> {
  const byMinute = new Map<number, ScheduledPost[]>();
  for (const p of posts) {
    const min = getPostCalendarDate(p)?.getMinutes() ?? 0;
    const arr = byMinute.get(min) ?? [];
    arr.push(p);
    byMinute.set(min, arr);
  }
  return byMinute;
}

function TimeRow({
  hour,
  weekDays,
  today,
  postsBySlot,
  onSlotClick,
  onPostClick,
  bestTimeLookup,
  bestTimePlatform,
  showBestTimes,
  showBestTimePercent,
  onDriveFileDrop,
}: {
  hour: number;
  weekDays: Date[];
  today: Date;
  postsBySlot: Map<string, ScheduledPost[]>;
  onSlotClick: (d: Date) => void;
  onPostClick: (p: ScheduledPost) => void;
  bestTimeLookup: Map<string, SchedulerBestTimeSlot>;
  bestTimePlatform: ScheduledPostPlatform;
  showBestTimes: boolean;
  showBestTimePercent: boolean;
  onDriveFileDrop: (file: SelectedCloudFile, slotDate: Date) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="h-16 flex items-start justify-end pr-3 pt-2 text-[10px] text-white/55 border-t border-white/[0.12] bg-muted font-medium">
        {hour}:00
      </div>
      {weekDays.map((day, di) => {
        const isToday = isSameDay(day, today);
        const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}-${hour}`;
        const slotPosts = postsBySlot.get(key) ?? [];
        const slotDate = new Date(day);
        slotDate.setHours(hour, 0, 0, 0);
        const bestTimeSlot = showBestTimes ? bestTimeLookup.get(`${di + 1}-${hour}`) : undefined;
        const [r, g, b] = BEST_TIME_HEATMAP_COLORS[bestTimePlatform];
        const bestTimeOpacity = bestTimeSlot
          ? Math.min(0.46, Math.max(0.05, bestTimeSlot.score / 180))
          : 0;
        const minuteGroups = groupPostsByMinute(slotPosts);
        const sortedMinutes = Array.from(minuteGroups.keys()).sort((a, b) => a - b);
        const visibleMinutes = sortedMinutes.slice(0, 2);
        const hiddenCount = sortedMinutes.length - visibleMinutes.length;
        return (
          <div
            key={di}
            onClick={() => onSlotClick(slotDate)}
            onDragOver={(event) => {
              if (!Array.from(event.dataTransfer.types).includes(DRIVE_DRAG_MIME)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "copy";
            }}
            onDrop={(event) => {
              const raw = event.dataTransfer.getData(DRIVE_DRAG_MIME);
              if (!raw) return;
              event.preventDefault();
              event.stopPropagation();
              try {
                onDriveFileDrop(JSON.parse(raw) as SelectedCloudFile, slotDate);
              } catch {
                // Ignore malformed drag payloads from outside the Drive panel.
              }
            }}
            className={`h-16 border-t border-l border-white/[0.12] cursor-pointer hover:bg-white/[0.07] transition-colors relative p-1 flex flex-col gap-0.5 overflow-hidden ${isToday ? "bg-[#ef3e29]/[0.08]" : "bg-card"}`}
          >
            {bestTimeSlot && (
              <>
                <span
                  className="absolute inset-0 pointer-events-none"
                  style={{ backgroundColor: `rgba(${r}, ${g}, ${b}, ${bestTimeOpacity})` }}
                />
                {showBestTimePercent && (
                  <span className="absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 text-[10px] italic text-black/28">
                    {bestTimeSlot.percent}%
                  </span>
                )}
              </>
            )}
            {visibleMinutes.map((min) => (
              <CalendarChip
                key={min}
                posts={minuteGroups.get(min)!}
                hour={hour}
                minute={min}
                onClick={onPostClick}
              />
            ))}
            {hiddenCount > 0 && (
              <span className="text-[8px] text-white/40 pl-1">
                {t("contentScheduler.moreItems", { count: hiddenCount })}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}

function CalendarChip({
  posts,
  hour,
  minute,
  onClick,
}: {
  posts: ScheduledPost[];
  hour: number;
  minute: number;
  onClick: (p: ScheduledPost) => void;
}) {
  const { t } = useTranslation();
  const dominantStatus = posts.reduce((best, p) => {
    return (STATUS_PRIORITY[p.status] ?? 0) > (STATUS_PRIORITY[best.status] ?? 0) ? p : best;
  }, posts[0]);
  const cfg = STATUS_CONFIG[dominantStatus.status] ?? STATUS_CONFIG.draft;
  const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const platforms = Array.from(new Set(posts.map((p) => p.platform)));
  const errorMsg = posts.find((p) => p.status === "failed" && p.errorMessage)?.errorMessage;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(posts[0]);
      }}
      title={errorMsg ? `${t("contentScheduler.errorPrefix")}${errorMsg}` : timeStr}
      className={`w-full rounded-md border px-1 py-0.5 flex items-center gap-1 overflow-hidden transition-colors text-left shadow-sm min-h-[20px] ${cfg.chipBg} ${cfg.chipBorder} hover:brightness-125`}
    >
      <span className="text-[8px] text-white/60 font-medium shrink-0 leading-none">{timeStr}</span>
      <span className="flex items-center gap-0.5 shrink-0">
        {platforms.map((pid) => {
          const opt = PLATFORM_OPTIONS.find((p) => p.id === pid);
          const Icon = opt?.icon ?? Facebook;
          return <Icon key={pid} size={8} style={{ color: opt?.color ?? "#fff" }} />;
        })}
      </span>
      <span className="text-[8px] text-white/70 truncate flex-1 leading-none">
        {posts[0].caption?.slice(0, 20)}
      </span>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ml-auto ${cfg.dotColor}`} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// PostListView
// ═══════════════════════════════════════════════════════

function PostListView({
  posts,
  postsLoading,
  onEdit,
  onPublish,
  onDelete,
  onDeleteBatch,
  onEditBatch,
  isPublishing,
}: {
  posts: ScheduledPost[];
  postsLoading: boolean;
  onEdit: (p: ScheduledPost) => void;
  onPublish: (id: string) => void;
  onDelete: (id: string) => void;
  onDeleteBatch: (batchId: string, count: number) => void;
  onEditBatch: (batchId: string) => void;
  isPublishing: boolean;
}) {
  const { t } = useTranslation();
  const batchSizes = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of posts) {
      const bid = (p as { batchId?: string | null }).batchId;
      if (bid) map.set(bid, (map.get(bid) ?? 0) + 1);
    }
    return map;
  }, [posts]);

  if (postsLoading)
    return (
      <div className="flex items-center justify-center py-20 text-white/30">
        <Loader2 className="animate-spin mr-2" size={16} /> {t("contentScheduler.loading")}
      </div>
    );
  if (posts.length === 0)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-white/30">
        <CalendarDays size={48} className="mb-4 text-white/10" />
        <p className="text-sm">{t("contentScheduler.noPosts")}</p>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-6 py-6 space-y-3">
      {posts.map((post) => {
        const status = STATUS_CONFIG[post.status] ?? STATUS_CONFIG.draft;
        const pOpt = PLATFORM_OPTIONS.find((p) => p.id === post.platform);
        const Icon = pOpt?.icon ?? Facebook;
        const color = pOpt?.color ?? "#1877F2";
        const canPublish =
          post.status === "draft" || post.status === "scheduled" || post.status === "failed";
        const canEdit = post.status !== "published" && post.status !== "publishing";
        const postDate = getPostCalendarDate(post);
        const batchId = (post as { batchId?: string | null }).batchId ?? null;
        const batchSize = batchId ? (batchSizes.get(batchId) ?? 0) : 0;
        return (
          <div
            key={post.id}
            className="flex items-center gap-4 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.1] transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
              <Icon size={18} style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${status.dotColor}`} />
                <span className="text-[10px] text-white/40 font-medium">{t(status.labelKey)}</span>
                {post.pageName && (
                  <span className="text-[10px] text-white/25">• {post.pageName}</span>
                )}
                {postDate && (
                  <span className="text-[10px] text-white/25">
                    •{" "}
                    {postDate.toLocaleString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
                {batchId && (
                  <span className="inline-flex items-center text-[9px] rounded bg-purple-500/15 text-purple-300 border border-purple-500/25 overflow-hidden">
                    <button
                      onClick={() => onEditBatch(batchId)}
                      className="px-1.5 py-0.5 hover:bg-purple-500/25 border-r border-purple-500/25"
                      title="Edytuj cały batch"
                    >
                      Batch · {batchSize} ✎
                    </button>
                    <button
                      onClick={() => onDeleteBatch(batchId, batchSize)}
                      className="px-1.5 py-0.5 hover:bg-red-500/25 hover:text-red-200"
                      title="Anuluj cały batch"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
              <p className="text-sm text-white/70 line-clamp-1">
                {post.caption || t("contentScheduler.noCaption")}
              </p>
              {post.errorMessage && (
                <p className="text-[10px] text-red-400/70 mt-1">
                  <AlertCircle size={9} className="inline mr-0.5" />
                  {post.errorMessage}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canPublish && (
                <button
                  onClick={() => onPublish(post.id)}
                  disabled={isPublishing}
                  className="p-2 text-[#ef3e29] bg-[#ef3e29]/10 rounded-lg hover:bg-[#ef3e29]/20 disabled:opacity-30"
                  title="Publikuj"
                >
                  <Send size={14} />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => onEdit(post)}
                  className="p-2 text-white/30 hover:text-white/60 rounded-lg hover:bg-white/[0.04]"
                  title="Edytuj"
                >
                  <CalendarDays size={14} />
                </button>
              )}
              <button
                onClick={() => onDelete(post.id)}
                className="p-2 text-white/20 hover:text-red-400 rounded-lg hover:bg-white/[0.04]"
                title={t("contentScheduler.modal.delete")}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CreatePostModal — editor (left) + preview (right)
// ═══════════════════════════════════════════════════════

function CreatePostModal({
  form,
  setForm,
  pages,
  pagesLoading,
  linkedInOrganizations,
  linkedInOrganizationsLoading,
  linkedInProfileName,
  connectedPlatforms,
  connectedPlatformDetails,
  connectablePlatforms,
  onConnectPlatform,
  onConnectInstagramDirect,
  onPageSelect,
  onLinkedInTargetSelect,
  onRemoveMediaUrl,
  onReorderMediaUrls,
  temporaryMedia,
  onLocalMediaSelect,
  onCloudMediaSelect,
  onRemoveTemporaryMedia,
  onSchedule,
  onSaveDraft,
  onPublishNow,
  onDuplicate,
  onCancel,
  isEditing,
  editingPostStatus,
  isSaving,
  forUserId,
}: {
  form: PostFormData;
  setForm: React.Dispatch<React.SetStateAction<PostFormData>>;
  pages: MetaPage[];
  pagesLoading: boolean;
  linkedInOrganizations: LinkedInOrganization[];
  linkedInOrganizationsLoading: boolean;
  linkedInProfileName: string;
  connectedPlatforms: ScheduledPostPlatform[];
  connectedPlatformDetails: ConnectedSchedulerPlatform[];
  connectablePlatforms: ScheduledPostPlatform[];
  onConnectPlatform: (platform: ScheduledPostPlatform) => Promise<void>;
  onConnectInstagramDirect: () => Promise<void>;
  onPageSelect: (id: string) => void;
  onLinkedInTargetSelect: (id: string) => void;
  onRemoveMediaUrl: (i: number) => void;
  onReorderMediaUrls: (mediaUrls: string[]) => void;
  temporaryMedia: TemporaryMediaItem[];
  onLocalMediaSelect: (files: FileList) => void;
  onCloudMediaSelect: (file: SelectedCloudFile) => void;
  onRemoveTemporaryMedia: (id: string) => void;
  onSchedule: () => void;
  onSaveDraft: () => void;
  onPublishNow: () => Promise<void>;
  onDuplicate: () => void;
  onCancel: () => void;
  isEditing: boolean;
  editingPostStatus: string;
  isSaving: boolean;
  forUserId?: string;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = (user as { role?: string } | undefined)?.role === "super_admin";
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const charLimit = CHAR_LIMITS[form.platform];
  const charCount = form.caption.length;
  const isOverLimit = charCount > charLimit;
  const selectedPage = pages.find((p) => p.id === form.pageId);
  const selectedLinkedInOrganization = linkedInOrganizations.find(
    (item) => item.id === form.pageId
  );
  const previewPageName =
    form.platform === "linkedin"
      ? form.pageId === "person"
        ? linkedInProfileName
        : selectedLinkedInOrganization?.name || form.pageName || linkedInProfileName
      : selectedPage?.name;
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showConnectMenu, setShowConnectMenu] = useState(false);
  const [openPlatformDropdown, setOpenPlatformDropdown] = useState<ScheduledPostPlatform | null>(
    null
  );
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ComposerAction>(
    isEditing ? "schedule" : form.scheduledAt ? "schedule" : "publish_now"
  );
  const [isPublishingNow, setIsPublishingNow] = useState(false);
  // Set by TikTokPresets when a runtime rule (e.g. video longer than the account's
  // max_video_post_duration_sec) must block the Post action.
  const [tiktokComposerBlocked, setTiktokComposerBlocked] = useState(false);
  const { data: composerBestTimesData } = useQuery<SchedulerBestTimesResponse>({
    queryKey: ["/api/scheduler/best-times", form.platform],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/scheduler/best-times?platform=${form.platform}`
      ) as Promise<SchedulerBestTimesResponse>,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const composerBestTimeSlots = useMemo(
    () => composerBestTimesData?.slots ?? BEST_TIME_FALLBACK_SLOTS[form.platform] ?? [],
    [composerBestTimesData?.slots, form.platform]
  );

  useEffect(() => {
    setSelectedAction(isEditing ? "schedule" : form.scheduledAt ? "schedule" : "publish_now");
    // Celowo tylko isEditing: to inicjalizacja akcji przy wejsciu w tryb edycji/nowego posta;
    // pozniejsza zmiana daty nie moze nadpisywac wyboru uzytkownika.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleAiGenerate = useCallback(async () => {
    if (!aiTopic.trim() || aiGenerating) return;
    setAiGenerating(true);
    try {
      const resp = (await apiRequest("POST", "/api/scheduler/generate-caption", {
        platform: form.platform,
        postType: form.postType,
        topic: aiTopic.trim(),
      })) as SchedulerCaptionResponse;
      if (resp.caption) {
        setForm((prev) => ({ ...prev, caption: resp.caption ?? prev.caption }));
        setShowAiPanel(false);
        setAiTopic("");
      }
    } catch {
      // error handled by apiRequest
    } finally {
      setAiGenerating(false);
    }
  }, [aiTopic, aiGenerating, form.platform, form.postType, setForm]);

  const visiblePlatforms = useMemo(() => {
    const platforms = connectedPlatforms.length > 0 ? connectedPlatforms : [];
    if (!platforms.includes(form.platform)) {
      return [form.platform, ...platforms];
    }
    return platforms;
  }, [connectedPlatforms, form.platform]);

  const handlePersistentMediaSelect = useCallback(
    (url: string, _mediaType: "image" | "video") => {
      setForm((prev) => {
        const mediaUrls = [...prev.mediaUrls, url];
        return { ...prev, mediaUrls, mediaType: deriveMediaType(mediaUrls) };
      });
    },
    [setForm]
  );

  const getPostTypeLabel = useCallback(
    (platform: ScheduledPostPlatform, postType: ScheduledMetaPostType): string => {
      if (platform === "youtube" && postType === "post") return "Video";
      if (platform === "youtube" && postType === "reel") return "Short";
      return META_POST_TYPE_LABELS[postType];
    },
    []
  );

  const handleUseBestTime = useCallback(() => {
    const nextBestTime = getNextBestTime(composerBestTimeSlots);
    if (!nextBestTime) return;
    setForm((prev) => ({ ...prev, scheduledAt: toLocalDatetimeValue(nextBestTime) }));
    setSelectedAction("schedule");
  }, [composerBestTimeSlots, setForm]);

  const chooseAction = useCallback((action: ComposerAction) => {
    setSelectedAction(action);
    setShowActionMenu(false);
  }, []);

  const runSelectedAction = useCallback(() => {
    if (selectedAction === "draft") {
      onSaveDraft();
      return;
    }
    if (selectedAction === "publish_now") {
      setIsPublishingNow(true);
      onPublishNow().finally(() => setIsPublishingNow(false));
      return;
    }
    onSchedule();
  }, [onPublishNow, onSaveDraft, onSchedule, selectedAction]);

  const actionMeta: Record<
    ComposerAction,
    { label: string; description: string; icon: React.ReactNode }
  > = {
    schedule: {
      label: isEditing ? "Zapisz zmiany" : "Zaplanuj",
      description: "Zapisz z wybraną datą",
      icon: <CalendarDays size={13} />,
    },
    draft: {
      label: "Zapisz szkic",
      description: "Zapisz bez publikowania",
      icon: <Check size={13} />,
    },
    publish_now: {
      label: "Opublikuj teraz",
      description: "Publikuj natychmiast",
      icon: <Send size={13} />,
    },
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] shrink-0">
        <div>
          <h2 className="text-base font-semibold text-white">
            {isEditing
              ? t("contentScheduler.modal.editTitle")
              : t("contentScheduler.modal.createTitle")}
          </h2>
          <p className="mt-1 text-xs text-white/35">{t("contentScheduler.modal.crosspostHint")}</p>
        </div>
        <button
          onClick={onCancel}
          className="p-1 text-white/40 hover:text-white/70 rounded-lg hover:bg-white/[0.06]"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* ── Left: Editor ── */}
        <div className="flex-1 flex flex-col border-r border-white/[0.06] min-w-0">
          {/* Platform + page */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-white/[0.04] shrink-0">
            {PLATFORM_OPTIONS.filter(
              (opt) =>
                visiblePlatforms.includes(opt.id) &&
                (isSuperAdmin || (opt.id !== "youtube" && opt.id !== "linkedin"))
            ).map((opt) => {
              const PIcon = opt.icon;
              const isSelected = opt.id in form.platformPostTypes;
              const selectedType = form.platformPostTypes[opt.id];
              const isDropdownOpen = openPlatformDropdown === opt.id;
              return (
                <div key={opt.id} className="relative flex flex-col items-center">
                  <button
                    onClick={() => {
                      setShowConnectMenu(false);
                      setOpenPlatformDropdown(isDropdownOpen ? null : opt.id);
                    }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all border-2 ${isSelected ? "border-white/40 scale-110" : "border-transparent opacity-40 hover:opacity-70"}`}
                    title={opt.label}
                  >
                    <PIcon size={16} style={{ color: opt.color }} />
                  </button>
                  {isSelected && selectedType && (
                    <span className="mt-0.5 text-[8px] text-white/40 font-medium uppercase tracking-wide leading-none">
                      {getPostTypeLabel(opt.id, selectedType).slice(0, 5)}
                    </span>
                  )}
                  {isDropdownOpen && (
                    <div className="absolute left-0 top-full mt-2 z-30 min-w-[130px] rounded-xl border border-white/[0.12] bg-popover shadow-2xl p-1">
                      {META_POST_TYPE_OPTIONS[opt.id].map((type) => (
                        <button
                          key={type}
                          onClick={() => {
                            const next: PostFormData = {
                              ...form,
                              platform: opt.id,
                              postType: type,
                              platformPostTypes: { ...form.platformPostTypes, [opt.id]: type },
                            };
                            if (type === "reel" || type === "trial_reel") next.mediaType = "video";
                            setForm(next);
                            setOpenPlatformDropdown(null);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${selectedType === type ? "bg-white/[0.1] text-white font-medium" : "text-white/70 hover:bg-white/[0.08]"}`}
                        >
                          {getPostTypeLabel(opt.id, type)}
                        </button>
                      ))}
                      {isSelected && (
                        <>
                          <div className="border-t border-white/[0.06] my-1" />
                          <button
                            onClick={() => {
                              setForm((prev) => {
                                const types = { ...prev.platformPostTypes };
                                delete types[opt.id];
                                const remaining = Object.keys(types) as ScheduledPostPlatform[];
                                return {
                                  ...prev,
                                  platformPostTypes: types,
                                  platform: remaining.length > 0 ? remaining[0] : prev.platform,
                                  postType:
                                    remaining.length > 0 ? types[remaining[0]]! : prev.postType,
                                };
                              });
                              setOpenPlatformDropdown(null);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg text-xs text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                          >
                            {t("contentScheduler.modal.deselect")}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Threads toggle (UI-only preview / presets) */}
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({ ...prev, threadsSelected: !prev.threadsSelected }))
              }
              title="Threads"
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all border-2 ${form.threadsSelected ? "border-white/40 scale-110" : "border-transparent opacity-40 hover:opacity-70"}`}
            >
              <AtSign size={16} className="text-white/85" />
            </button>
            <div className="relative">
              <button
                onClick={() => {
                  setOpenPlatformDropdown(null);
                  setShowConnectMenu((prev) => !prev);
                }}
                className="ml-0.5 p-1.5 text-white/65 hover:text-white hover:bg-white/[0.06] rounded-md transition-colors"
                title={t("contentScheduler.modal.connectPlatform")}
              >
                <Plus size={14} />
              </button>
              {showConnectMenu && (
                <div className="absolute left-0 top-[110%] z-30 w-72 rounded-xl border border-white/[0.12] bg-popover shadow-2xl p-2">
                  {connectablePlatforms.length === 0 && (
                    <div className="px-2.5 py-2 text-[11px] text-white/50">
                      {t("contentScheduler.modal.allPlatformsConnected")}
                    </div>
                  )}
                  {connectablePlatforms.map((platform) => {
                    const platformInfo = PLATFORM_CONNECT_CONFIG[platform];
                    const platformOption = PLATFORM_OPTIONS.find((item) => item.id === platform);
                    const Icon = platformOption?.icon ?? Facebook;
                    const canConnect = Boolean(platformInfo.connectKey);
                    return (
                      <button
                        key={platform}
                        onClick={async () => {
                          if (!canConnect) return;
                          setShowConnectMenu(false);
                          await onConnectPlatform(platform);
                        }}
                        disabled={!canConnect}
                        className={`w-full text-left rounded-lg px-2.5 py-2.5 transition-colors ${canConnect ? "hover:bg-white/[0.08]" : "opacity-60 cursor-not-allowed"}`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-white/70" />
                          <span className="text-xs font-medium text-white/85">
                            {t(platformInfo.connectLabelKey)}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[10px] text-white/45">
                          {t(platformInfo.connectDescriptionKey)}
                        </div>
                      </button>
                    );
                  })}
                  {/* Second way to connect Instagram: direct Instagram Business login (no FB Page) */}
                  <button
                    key="instagram_direct"
                    onClick={async () => {
                      setShowConnectMenu(false);
                      await onConnectInstagramDirect();
                    }}
                    className="w-full text-left rounded-lg px-2.5 py-2.5 transition-colors hover:bg-white/[0.08]"
                  >
                    <div className="flex items-center gap-2">
                      <Instagram size={14} className="text-white/70" />
                      <span className="text-xs font-medium text-white/85">
                        {t("contentScheduler.platformConnect.instagram_direct.label")}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-white/45">
                      {t("contentScheduler.platformConnect.instagram_direct.description")}
                    </div>
                  </button>
                </div>
              )}
            </div>
            <div className="ml-auto">
              {form.platform === "facebook" || form.platform === "instagram" ? (
                pagesLoading ? (
                  <span className="text-[10px] text-white/30">
                    <Loader2 className="animate-spin inline" size={10} />{" "}
                    {t("contentScheduler.loading")}
                  </span>
                ) : (
                  (() => {
                    const isIG = form.platform === "instagram";
                    const availablePages = isIG
                      ? pages.filter((p) => p.instagramBusinessAccount)
                      : pages;
                    if (availablePages.length === 0) {
                      return (
                        <span className="text-[10px] text-yellow-400">
                          {isIG
                            ? t("contentScheduler.modal.noIgAccounts")
                            : t("contentScheduler.modal.connectMeta")}
                        </span>
                      );
                    }
                    return (
                      <select
                        value={form.pageId}
                        onChange={(e) => onPageSelect(e.target.value)}
                        className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#ef3e29]/50 max-w-[220px]"
                      >
                        <option value="">
                          {isIG
                            ? t("contentScheduler.modal.selectIgAccount")
                            : t("contentScheduler.modal.selectPage")}
                        </option>
                        {availablePages.map((page) => (
                          <option key={page.id} value={page.id}>
                            {isIG
                              ? `@${page.instagramBusinessAccount?.username || page.instagramBusinessAccount?.name || page.name}`
                              : page.name}
                          </option>
                        ))}
                      </select>
                    );
                  })()
                )
              ) : form.platform === "linkedin" ? (
                linkedInOrganizationsLoading ? (
                  <span className="text-[10px] text-white/30">
                    <Loader2 className="animate-spin inline" size={10} /> Ładowanie...
                  </span>
                ) : (
                  <select
                    value={form.pageId || "person"}
                    onChange={(e) => onLinkedInTargetSelect(e.target.value)}
                    className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#ef3e29]/50 max-w-[260px]"
                  >
                    <option value="person">Profil: {linkedInProfileName}</option>
                    {linkedInOrganizations.map((organization) => (
                      <option key={organization.id} value={organization.id}>
                        Firma: {organization.name}
                      </option>
                    ))}
                  </select>
                )
              ) : (
                (() => {
                  const detail = connectedPlatformDetails.find(
                    (item) => item.platform === form.platform && item.connected
                  );
                  if (!detail) {
                    return (
                      <span className="text-[10px] text-yellow-400">
                        {t("contentScheduler.modal.connectPlatform")}
                      </span>
                    );
                  }
                  if (form.platform === "youtube") {
                    return (
                      <span className="inline-flex items-center gap-1.5 text-[10px] text-white/40">
                        {detail.accountThumbnail ? (
                          <img
                            src={detail.accountThumbnail}
                            alt={detail.accountName ?? ""}
                            className="w-4 h-4 rounded-full object-cover bg-white/10"
                          />
                        ) : (
                          <Youtube size={12} className="text-[#FF0000]" />
                        )}
                        {t("contentScheduler.modal.publishOnYoutubeChannel", {
                          name: detail.accountName ?? "",
                        })}
                      </span>
                    );
                  }
                  if (form.platform === "tiktok") {
                    return (
                      <span className="text-[10px] text-white/40">
                        {t("contentScheduler.modal.publishOnTiktokAccount", {
                          name: detail.accountName ? `@${detail.accountName}` : "",
                        })}
                      </span>
                    );
                  }
                  return null;
                })()
              )}
            </div>
          </div>

          {/* Textarea */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {(form.platform === "youtube" || "youtube" in form.platformPostTypes) && (
              <div className="mb-3">
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value.slice(0, 100) }))
                  }
                  placeholder="Tytuł filmu (YouTube) — jeśli puste, użyjemy pierwszej linii opisu"
                  maxLength={100}
                  className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-white/30"
                />
                <div className="text-[10px] text-white/30 mt-1 text-right">
                  {form.title.length}/100
                </div>
              </div>
            )}
            <div className="relative w-full h-full min-h-[250px]">
              <textarea
                ref={captionRef}
                value={form.caption}
                onChange={(e) => setForm((prev) => ({ ...prev, caption: e.target.value }))}
                placeholder={t("contentScheduler.modal.captionPlaceholder")}
                className="w-full h-full min-h-[250px] bg-transparent text-sm text-white/90 placeholder:text-white/20 focus:outline-none resize-none leading-relaxed pr-10"
              />
              <div className="absolute bottom-2 right-2">
                <VoiceButton targetRef={captionRef} />
              </div>
            </div>
            {/* Media thumbnails */}
            {(form.mediaUrls.length > 0 || temporaryMedia.length > 0) && (
              <div className="space-y-3 mt-4 pb-2">
                {form.mediaUrls.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/25">
                        {t("contentScheduler.modal.savedAssets")}
                      </div>
                      {form.mediaType === "carousel" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.08] px-2 py-0.5 text-[9px] uppercase tracking-wide text-white/60">
                          <GalleryHorizontalEnd size={9} /> Karuzela · {form.mediaUrls.length}
                        </span>
                      )}
                      {form.mediaUrls.length > 1 && (
                        <span className="text-[9px] text-white/25">
                          {t("contentScheduler.modal.dragToReorder")}
                        </span>
                      )}
                    </div>
                    {/* Kolejność = kolejność publikacji. Przeciągnij, by zmienić. */}
                    <Reorder.Group
                      axis="x"
                      values={form.mediaUrls}
                      onReorder={onReorderMediaUrls}
                      className="flex gap-2 overflow-x-auto pb-1"
                    >
                      {form.mediaUrls.map((url, i) => (
                        <Reorder.Item
                          key={url}
                          value={url}
                          className="relative group shrink-0 cursor-grab active:cursor-grabbing"
                        >
                          <div className="w-16 h-16 rounded-lg bg-white/[0.06] border border-white/[0.08] overflow-hidden">
                            {isVideoMediaUrl(url) ? (
                              <video
                                src={url}
                                className="w-full h-full object-cover pointer-events-none"
                                muted
                                playsInline
                              />
                            ) : (
                              <img
                                src={url}
                                alt=""
                                className="w-full h-full object-cover pointer-events-none"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            )}
                          </div>
                          {form.mediaUrls.length > 1 && (
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/60 px-1 text-[8px] font-medium text-white/85">
                              {i + 1}
                            </span>
                          )}
                          <button
                            onClick={() => onRemoveMediaUrl(i)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={9} className="text-white" />
                          </button>
                        </Reorder.Item>
                      ))}
                    </Reorder.Group>
                  </div>
                )}

                {temporaryMedia.length > 0 && (
                  <div>
                    {temporaryMedia.some((item) => item.source === "cloud") && (
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60">
                          {t("contentScheduler.modal.cloudFiles")}
                        </div>
                        <div className="text-[10px] text-amber-200/45">
                          {t("contentScheduler.modal.driveImportInProgress")}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {temporaryMedia.map((item) => (
                        <div
                          key={item.id}
                          className={`group relative w-[168px] rounded-xl border p-2 text-left ${
                            item.uploadError
                              ? "border-red-400/30 bg-red-500/[0.06]"
                              : item.uploading
                                ? "border-white/[0.08] bg-white/[0.03]"
                                : "border-amber-400/15 bg-amber-500/[0.06]"
                          }`}
                        >
                          <div className="flex gap-2">
                            <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/[0.05] flex items-center justify-center shrink-0 relative">
                              {item.previewUrl && isRenderableUrl(item.previewUrl) ? (
                                item.mediaType === "video" ? (
                                  <video
                                    src={item.previewUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                  />
                                ) : (
                                  <img
                                    src={item.previewUrl}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                )
                              ) : item.mediaType === "video" ? (
                                <Film size={18} className="text-white/40" />
                              ) : (
                                <ImagePlus size={18} className="text-white/40" />
                              )}
                              {item.uploading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                                  <Loader2 size={18} className="animate-spin text-white" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] text-white/80 truncate">{item.name}</div>
                              {item.uploading && (
                                <div className="text-[10px] text-blue-400/80 mt-1">
                                  {t("contentScheduler.modal.uploading")}
                                  {item.source === "local" && typeof item.progress === "number"
                                    ? ` ${item.progress}%`
                                    : ""}
                                </div>
                              )}
                              {item.uploading &&
                                item.source === "local" &&
                                typeof item.progress === "number" && (
                                  <div className="mt-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-400/80 rounded-full transition-all"
                                      style={{ width: `${item.progress}%` }}
                                    />
                                  </div>
                                )}
                              {item.uploadError && (
                                <div className="text-[10px] text-red-400/80 mt-1">
                                  {item.uploadError}
                                </div>
                              )}
                              {!item.uploading && !item.uploadError && (
                                <div className="text-[10px] text-white/35 mt-1">
                                  {item.source === "cloud"
                                    ? "Google Drive"
                                    : t("contentScheduler.modal.waiting")}
                                </div>
                              )}
                              {item.sizeLabel && (
                                <div className="text-[10px] text-white/25 mt-0.5">
                                  {item.sizeLabel}
                                </div>
                              )}
                            </div>
                          </div>
                          {!item.uploading && (
                            <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-white/65">
                              {item.mediaType === "video" ? (
                                <Video size={9} />
                              ) : (
                                <ImagePlus size={9} />
                              )}
                              {item.source === "cloud"
                                ? "cloud"
                                : item.uploadError
                                  ? t("contentScheduler.status.failed").toLowerCase()
                                  : "temp"}
                            </span>
                          )}
                          {!item.uploading && (
                            <button
                              type="button"
                              onClick={() => onRemoveTemporaryMedia(item.id)}
                              className="absolute right-2 top-2 w-5 h-5 rounded-full bg-red-500/85 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={10} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Presets */}
            <div className="mt-6">
              <PresetsPanel
                presets={form.presets}
                selectedPlatforms={Object.keys(form.platformPostTypes) as ScheduledPostPlatform[]}
                threadsSelected={form.threadsSelected}
                onChange={(next) => setForm((prev) => ({ ...prev, presets: next }))}
                tiktokForUserId={forUserId}
                tiktokIsPhoto={form.mediaType === "image" || form.mediaType === "carousel"}
                tiktokVideoUrl={form.mediaType === "video" ? form.mediaUrls[0] : undefined}
                tiktokOnBlockedChange={setTiktokComposerBlocked}
              />
            </div>
          </div>

          {/* Toolbar */}
          <div className="px-6 py-3 border-t border-white/[0.04] flex items-center gap-0.5 shrink-0 relative">
            <button
              onClick={() => setShowMediaPicker((prev) => !prev)}
              className={`p-2 rounded-lg transition-colors ${showMediaPicker ? "text-[#ef3e29] bg-[#ef3e29]/10" : "text-white/30 hover:text-white/60 hover:bg-white/[0.04]"}`}
              title={t("contentScheduler.modal.addMedia")}
            >
              <ImagePlus size={16} />
            </button>
            <MediaPicker
              open={showMediaPicker}
              onClose={() => setShowMediaPicker(false)}
              onSelect={handlePersistentMediaSelect}
              onLocalMediaSelect={onLocalMediaSelect}
              onCloudMediaSelect={onCloudMediaSelect}
            />
            <button
              className="p-2 text-white/30 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors"
              title="Emoji"
            >
              <Smile size={16} />
            </button>
            <button
              className="p-2 text-white/30 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors"
              title="Lokalizacja"
            >
              <MapPin size={16} />
            </button>
            <button
              className="p-2 text-white/30 hover:text-white/60 hover:bg-white/[0.04] rounded-lg transition-colors"
              title="Link"
            >
              <Link2 size={16} />
            </button>
            <button
              onClick={() => setShowAiPanel((prev) => !prev)}
              className={`p-2 rounded-lg transition-colors ${showAiPanel ? "text-purple-400 bg-purple-500/10" : "text-white/30 hover:text-purple-400 hover:bg-purple-500/10"}`}
              title={t("contentScheduler.modal.generateAi")}
            >
              <Sparkles size={16} />
            </button>
            {showAiPanel && (
              <div className="absolute bottom-full left-0 mb-2 w-[360px] bg-popover border border-white/[0.1] rounded-xl shadow-2xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                  <Sparkles size={14} className="text-purple-400" />
                  <span className="text-xs font-medium text-white/80">
                    {t("contentScheduler.modal.generateAi")}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  <textarea
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder={t("contentScheduler.modal.aiTopicPlaceholder")}
                    className="w-full h-20 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white/90 placeholder:text-white/20 focus:outline-none focus:border-purple-500/50 resize-none"
                  />
                  {aiGenerating ? (
                    <AiGeneratingOverlay
                      compact
                      message={t("contentScheduler.modal.generatingCaption")}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-white/30">
                        {PLATFORM_OPTIONS.find((p) => p.id === form.platform)?.label} ·{" "}
                        {META_POST_TYPE_LABELS[form.postType]}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowAiPanel(false);
                            setAiTopic("");
                          }}
                          className="px-3 py-1.5 text-[11px] text-white/40 hover:text-white/60 rounded-lg hover:bg-white/[0.04]"
                        >
                          {t("contentScheduler.modal.cancel")}
                        </button>
                        <button
                          onClick={handleAiGenerate}
                          disabled={!aiTopic.trim() || aiGenerating}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-[11px] font-medium rounded-lg hover:bg-purple-500 transition-colors disabled:opacity-30"
                        >
                          <Sparkles size={12} />
                          {t("contentScheduler.modal.generate")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <span className="ml-auto" />
            <span className={`text-[11px] mr-1 ${isOverLimit ? "text-red-400" : "text-white/25"}`}>
              {charCount.toLocaleString()} / {charLimit.toLocaleString()}
            </span>
            {(() => {
              const opt = PLATFORM_OPTIONS.find((p) => p.id === form.platform);
              if (!opt) return null;
              const PIcon = opt.icon;
              return <PIcon size={11} style={{ color: opt.color }} />;
            })()}
          </div>

          {/* Footer */}
          {(() => {
            const selectedPlatforms = Object.keys(
              form.platformPostTypes
            ) as ScheduledPostPlatform[];
            const needsMeta = selectedPlatforms.some((p) => p === "facebook" || p === "instagram");
            const metaMissing = needsMeta && !form.pageId;
            const noPlatform = selectedPlatforms.length === 0;
            const noCaption = !form.caption.trim();
            const alreadyPublished =
              editingPostStatus === "published" || editingPostStatus === "publishing";
            const missingScheduleTime = selectedAction === "schedule" && !form.scheduledAt;
            const isUploading = temporaryMedia.some((m) => m.uploading);
            // TikTok Content Sharing Guidelines: privacy must be chosen, and if the
            // commercial-disclosure toggle is on a content type must be selected —
            // otherwise the Post action must stay disabled.
            const tiktokSelected = selectedPlatforms.includes("tiktok");
            const tiktokValidity = tiktokSelected ? getTikTokValidity(form.presets.tiktok) : null;
            const tiktokBlocked = tiktokSelected && tiktokComposerBlocked;
            const tiktokInvalid = tiktokValidity !== null || tiktokBlocked;
            const actionDisabled =
              isSaving ||
              isPublishingNow ||
              noCaption ||
              noPlatform ||
              metaMissing ||
              alreadyPublished ||
              missingScheduleTime ||
              isUploading ||
              tiktokInvalid;
            const menuDisabled =
              isSaving ||
              isPublishingNow ||
              noCaption ||
              noPlatform ||
              metaMissing ||
              alreadyPublished ||
              isUploading ||
              tiktokInvalid;
            const currentAction = actionMeta[selectedAction];
            return (
              <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between shrink-0 gap-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm text-white/40 hover:text-white/60 rounded-lg hover:bg-white/[0.04] transition-colors"
                  >
                    {t("contentScheduler.modal.cancel")}
                  </button>
                  {metaMissing && (
                    <span className="text-[11px] text-yellow-400 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {t("contentScheduler.modal.selectPageOrIg")}
                    </span>
                  )}
                  {tiktokValidity && (
                    <span className="text-[11px] text-yellow-400 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {t(`contentScheduler.presets.tiktok.gate.${tiktokValidity}`)}
                    </span>
                  )}
                  {isUploading && (
                    <span className="text-[11px] text-blue-400/80 flex items-center gap-1">
                      <Loader2 size={11} className="animate-spin" />
                      {t("contentScheduler.modal.uploading")}
                    </span>
                  )}
                </div>

                {isEditing ? (
                  /* ── Edit mode: Save changes + dropdown (Duplicate / Publish now) ── */
                  <div className="flex items-center gap-2">
                    {/* Datetime picker */}
                    <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2">
                      <CalendarDays size={13} className="text-white/30" />
                      <input
                        type="datetime-local"
                        value={form.scheduledAt}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, scheduledAt: e.target.value }))
                        }
                        className="bg-transparent text-xs text-white/70 focus:outline-none w-[155px]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleUseBestTime}
                      disabled={composerBestTimeSlots.length === 0 || alreadyPublished}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-300 hover:bg-blue-500/20 disabled:opacity-30 transition-colors"
                      title={t("contentScheduler.modal.bestTimeTitle")}
                    >
                      <Sparkles size={12} />
                      {t("contentScheduler.modal.bestTime")}
                    </button>
                    {/* Edit action dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setShowActionMenu((prev) => !prev)}
                        className="px-3 py-2 text-xs text-white/50 bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.08] transition-colors flex items-center gap-1"
                      >
                        <List size={12} />
                      </button>
                      {showActionMenu && (
                        <div className="absolute bottom-full right-0 mb-2 w-52 rounded-xl border border-white/[0.12] bg-popover shadow-2xl overflow-hidden z-50">
                          <button
                            onClick={() => chooseAction("schedule")}
                            disabled={
                              alreadyPublished ||
                              isSaving ||
                              isPublishingNow ||
                              noCaption ||
                              noPlatform ||
                              metaMissing
                            }
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">Zapisz zmiany</div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              Zapisz z wybraną datą
                            </div>
                          </button>
                          <div className="border-t border-white/[0.06]" />
                          <button
                            onClick={() => {
                              setShowActionMenu(false);
                              onDuplicate();
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">
                              {t("contentScheduler.modal.duplicatePost")}
                            </div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              {t("contentScheduler.modal.duplicateHint")}
                            </div>
                          </button>
                          <div className="border-t border-white/[0.06]" />
                          <button
                            onClick={() => chooseAction("publish_now")}
                            disabled={
                              alreadyPublished ||
                              isSaving ||
                              isPublishingNow ||
                              noCaption ||
                              noPlatform ||
                              metaMissing
                            }
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">
                              {t("contentScheduler.modal.publishNow")}
                            </div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              {alreadyPublished
                                ? t("contentScheduler.modal.alreadyPublished")
                                : t("contentScheduler.modal.publishImmediately")}
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Save changes — blocked for published/publishing posts */}
                    <button
                      onClick={runSelectedAction}
                      disabled={actionDisabled}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#ef3e29] text-white text-sm font-medium rounded-lg hover:bg-[#d63520] transition-colors disabled:opacity-30 shadow-lg shadow-[#ef3e29]/20"
                    >
                      {isSaving || isPublishingNow ? (
                        <Loader2 className="animate-spin" size={13} />
                      ) : (
                        currentAction.icon
                      )}
                      {currentAction.label}
                    </button>
                  </div>
                ) : (
                  /* ── Create mode: datetime + dropdown (Draft / Publish now) + main button ── */
                  <div className="flex items-center gap-2">
                    {/* Datetime picker */}
                    <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2">
                      <CalendarDays size={13} className="text-white/30" />
                      <input
                        type="datetime-local"
                        value={form.scheduledAt}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, scheduledAt: e.target.value }))
                        }
                        className="bg-transparent text-xs text-white/70 focus:outline-none w-[155px]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleUseBestTime}
                      disabled={composerBestTimeSlots.length === 0}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-300 hover:bg-blue-500/20 disabled:opacity-30 transition-colors"
                      title={t("contentScheduler.modal.bestTimeTitle")}
                    >
                      <Sparkles size={12} />
                      {t("contentScheduler.modal.bestTime")}
                    </button>
                    {/* Create action dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setShowActionMenu((prev) => !prev)}
                        disabled={menuDisabled}
                        className="px-3 py-2 text-xs text-white/50 bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.08] transition-colors disabled:opacity-30 flex items-center gap-1"
                      >
                        <List size={12} />
                      </button>
                      {showActionMenu && (
                        <div className="absolute bottom-full right-0 mb-2 w-52 rounded-xl border border-white/[0.12] bg-popover shadow-2xl overflow-hidden z-50">
                          <button
                            onClick={() => chooseAction("schedule")}
                            disabled={isSaving || noCaption || noPlatform || metaMissing}
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">Zaplanuj</div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              Zapisz z wybraną datą
                            </div>
                          </button>
                          <div className="border-t border-white/[0.06]" />
                          <button
                            onClick={() => chooseAction("draft")}
                            disabled={isSaving || noCaption || noPlatform}
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">
                              {t("contentScheduler.modal.saveDraft")}
                            </div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              {t("contentScheduler.modal.saveDraftHint")}
                            </div>
                          </button>
                          <div className="border-t border-white/[0.06]" />
                          <button
                            onClick={() => chooseAction("publish_now")}
                            disabled={menuDisabled}
                            className="w-full text-left px-4 py-3 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                          >
                            <div className="text-xs font-medium text-white/80">
                              {t("contentScheduler.modal.publishNow")}
                            </div>
                            <div className="text-[10px] text-white/35 mt-0.5">
                              {t("contentScheduler.modal.publishImmediately")}
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Main action button */}
                    <button
                      onClick={runSelectedAction}
                      disabled={actionDisabled}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#ef3e29] text-white text-sm font-medium rounded-lg hover:bg-[#d63520] transition-colors disabled:opacity-30 shadow-lg shadow-[#ef3e29]/20"
                    >
                      {isSaving || isPublishingNow ? (
                        <Loader2 className="animate-spin" size={13} />
                      ) : (
                        currentAction.icon
                      )}
                      {currentAction.label}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ── Right: Preview ── */}
        <div className="w-[380px] shrink-0 max-lg:hidden">
          <PreviewPanel
            form={form}
            selectedPlatforms={Object.keys(form.platformPostTypes) as ScheduledPostPlatform[]}
            threadsSelected={form.threadsSelected}
            pageName={selectedPage?.name}
            pageId={form.pageId}
            youtubeChannelName={
              connectedPlatformDetails.find((d) => d.platform === "youtube" && d.connected)
                ?.accountName ?? undefined
            }
            youtubeChannelAvatarUrl={
              connectedPlatformDetails.find((d) => d.platform === "youtube" && d.connected)
                ?.accountThumbnail ?? undefined
            }
            temporaryMedia={temporaryMedia}
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MediaPicker — file upload + graphics library picker
// ═══════════════════════════════════════════════════════

interface LibraryGraphic {
  id: string;
  filename: string;
  signedUrl: string | null;
  mediaType: "image" | "video" | null;
  format: string;
}

type MediaPickerTab = "local" | "library" | "cloud";

function MediaPicker({
  open,
  onClose,
  onSelect,
  onLocalMediaSelect,
  onCloudMediaSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, mediaType: "image" | "video") => void;
  onLocalMediaSelect: (files: FileList) => void;
  onCloudMediaSelect: (file: SelectedCloudFile) => void;
}) {
  const [tab, setTab] = useState<MediaPickerTab>("local");
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const selectedClientUserId = useStore((s) => s.selectedClientUserId);

  const libraryUrl = selectedClientUserId
    ? `/api/graphics?pageSize=50&contentType=organic&forUserId=${encodeURIComponent(selectedClientUserId)}`
    : "/api/graphics?pageSize=50&contentType=organic";

  const { data: libraryData, isLoading: libraryLoading } = useQuery<GraphicsLibraryResponse>({
    queryKey: ["graphics-library-picker-organic", selectedClientUserId ?? null],
    queryFn: () => apiRequest("GET", libraryUrl) as Promise<GraphicsLibraryResponse>,
    enabled: open,
    staleTime: 30_000,
  });
  const libraryItems: LibraryGraphic[] = libraryData?.items ?? [];

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        onLocalMediaSelect(e.target.files);
        onClose();
        e.target.value = "";
      }
    },
    [onClose, onLocalMediaSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files) {
        onLocalMediaSelect(e.dataTransfer.files);
        onClose();
      }
    },
    [onClose, onLocalMediaSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  if (!open) return null;

  return (
    <div className="absolute bottom-full left-0 mb-2 w-[360px] bg-popover border border-white/[0.1] rounded-xl shadow-2xl z-50 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-white/[0.06]">
        <button
          onClick={() => setTab("local")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${tab === "local" ? "text-white border-b-2 border-[#ef3e29]" : "text-white/40 hover:text-white/60"}`}
        >
          <Upload size={13} /> Local
        </button>
        <button
          onClick={() => setTab("library")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${tab === "library" ? "text-white border-b-2 border-[#ef3e29]" : "text-white/40 hover:text-white/60"}`}
        >
          <FolderOpen size={13} /> Library
        </button>
        <button
          onClick={() => setTab("cloud")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${tab === "cloud" ? "text-white border-b-2 border-[#ef3e29]" : "text-white/40 hover:text-white/60"}`}
        >
          <Cloud size={13} /> Drive
        </button>
      </div>

      {/* Content */}
      {tab === "local" && (
        <div className="p-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileInput}
            className="hidden"
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="w-full py-8 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer border-white/[0.1] hover:border-white/[0.2] hover:bg-white/[0.02]"
          >
            <>
              <Upload className="mx-auto mb-2 text-white/20" size={24} />
              <p className="text-xs text-white/50">
                {t("contentScheduler.mediaPicker.clickOrDrag")}
              </p>
              <p className="text-[10px] text-white/25 mt-1">
                {t("contentScheduler.mediaPicker.sessionOnly")}
              </p>
            </>
          </div>
        </div>
      )}

      {tab === "library" && (
        <div className="p-3 max-h-[280px] overflow-y-auto">
          {libraryLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="animate-spin mx-auto text-white/30" size={20} />
            </div>
          ) : libraryItems.length === 0 ? (
            <p className="py-8 text-center text-xs text-white/30">
              {t("contentScheduler.mediaPicker.noLibraryItems")}
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {libraryItems.map((item) => {
                if (!item.signedUrl) return null;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      onSelect(item.signedUrl!, item.mediaType === "video" ? "video" : "image");
                      onClose();
                    }}
                    className="aspect-square rounded-lg bg-white/[0.04] border border-white/[0.06] overflow-hidden hover:border-[#ef3e29]/50 hover:scale-105 transition-all group relative"
                  >
                    <img
                      src={item.signedUrl}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {item.mediaType === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Video size={16} className="text-white/80" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "cloud" && (
        <div className="p-4 space-y-3">
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Cloud size={15} className="text-white/45" />
              Google Drive
            </div>
            <p className="mt-2 text-xs text-white/40 leading-relaxed">
              {t("contentScheduler.mediaPicker.driveDescription")}
            </p>
            <button
              onClick={() => setCloudPickerOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2 text-xs text-white/75 hover:bg-white/[0.07] transition-colors"
            >
              <Cloud size={13} />
              {t("contentScheduler.mediaPicker.openDrive")}
            </button>
          </div>
        </div>
      )}

      {/* Close */}
      <div className="px-3 py-2 border-t border-white/[0.06] flex justify-end">
        <button
          onClick={onClose}
          className="text-[10px] text-white/30 hover:text-white/50 px-2 py-1"
        >
          {t("contentScheduler.mediaPicker.close")}
        </button>
      </div>

      <CloudStoragePicker
        open={cloudPickerOpen}
        onClose={() => setCloudPickerOpen(false)}
        accept="all"
        mode="select"
        onFileSelected={(file) => {
          onCloudMediaSelect(file);
          setCloudPickerOpen(false);
          onClose();
        }}
      />
    </div>
  );
}
